// ═══════════════════════════════════════════════════════════════════
// ieRig.js — THE IE HUMANOID RIG, runtime (IEH1)   (caP'n, 2026-09-27)
// ═══════════════════════════════════════════════════════════════════
// caP'n: "I want her so rigged up she can do the worm dance on the floor,
// squidward squiggle dance, the robot … walking, dancing, the lean back,
// sitting, crossed legged, thinking, running punching … so that making a
// cutscene and having her do anything would be a piece of cake … not just
// for her … every single character gets the benefit".
//
// ONE FILE, ANY IE HUMANOID. Everything being-specific lives IN THE GLB
// (node extras `ieh`, written by gamemaster/models/sculpt/tools/ieh_rigup.py):
// the drivers, the colliders measured off her skin, the chains, the sockets,
// the floor-contact points. A glb without `ieh` gets a rig that does nothing.
//
//   const rig = makeRig(root, B, { hair: true })   // once per mounted body
//   each frame:
//     rig.begin()            // FIRST: puts back what the post layers changed
//     …the game's idle/walk, the mood layer…
//     rig.apply(t, dt, ctl)  // the CLIP layer (ctl.clip), IK, ground
//     rig.end(dt)            // drivers (clavicles, twists) + chains (skirt, tail, hair)
//
// THE PARITY LAW: every bone the post layers write is restored in begin(),
// so no layer ever builds on its own last frame (the desktop companion never
// re-sets most bones; a tail that wound up there taught us this).
//
// CLIPS are data (ieMoves.js): keyframes of named poses in DEGREES, eases,
// hips/root offsets, hand shapes, face, IK goals, ground contact — or a
// generator fn for the loopy dances. Axes (identity rest, arms hanging):
//   +x = her LEFT, +y up, +z forward.  thigh x<0 = leg forward; shin x>0 =
//   knee bend; upperarm x<0 = arm forward/up, z>0 = LEFT arm out (R: z<0);
//   forearm x<0 = elbow bend; spine/chest x>0 = bend forward, x<0 = arch
//   back; head x>0 = nod down, y>0 = turn to her left, z>0 = tilt right.
//
// PERF LAW: nothing allocates per frame after the first; no React state.
// ═══════════════════════════════════════════════════════════════════
import * as THREE from "three";

const D2R = Math.PI / 180;
const V = () => new THREE.Vector3();
const Qn = () => new THREE.Quaternion();
const _v1 = V(), _v2 = V(), _v3 = V(), _v7 = V();
const _q1 = Qn(), _q2 = Qn(), _q3 = Qn();
const _e = new THREE.Euler();
const QI = new THREE.Quaternion();
const DOWN = new THREE.Vector3(0, -1, 0);

export const EASE = {
  lin: (u) => u,
  io: (u) => u * u * (3 - 2 * u),
  in: (u) => u * u * u,
  out: (u) => 1 - (1 - u) ** 3,
  step: (u) => (u < 1 ? 0 : 1),
  // the robot: stays, then snaps with a tiny overshoot and settles
  snap: (u) => { if (u < 0.55) return 0; const k = (u - 0.55) / 0.45; return 1 + Math.sin(k * Math.PI * 1.5) * 0.08 * (1 - k) - (1 - k) ** 6; },
  back: (u) => { const c = 1.7; return 1 + (c + 1) * (u - 1) ** 3 + c * (u - 1) ** 2; },
};

// ── helpers ──────────────────────────────────────────────────────────
const qDeg = (a, out) => out.setFromEuler(_e.set((a[0] || 0) * D2R, (a[1] || 0) * D2R, (a[2] || 0) * D2R, "XYZ"));
function twistAngle(q, ax) {
  const p = q.x * ax.x + q.y * ax.y + q.z * ax.z;
  let a = 2 * Math.atan2(p, q.w);
  if (a > Math.PI) a -= 2 * Math.PI; else if (a < -Math.PI) a += 2 * Math.PI;
  return a;
}
const SIDE = /(L|R)(\d*)$/;
export function mirrorName(n) {
  // thighL ↔ thighR, dreadL1 ↔ dreadR1, skirt_L2_3 ↔ skirt_R2_3
  if (/^skirt_[LR]/.test(n)) return n.replace(/^skirt_([LR])/, (m, s) => "skirt_" + (s === "L" ? "R" : "L"));
  const m = n.match(SIDE);
  if (!m) return n;
  return n.slice(0, m.index) + (m[1] === "L" ? "R" : "L") + m[2];
}
// a pose seen in a mirror (swap sides, flip y/z rotations, flip x offsets)
export function mirrorPose(P) {
  if (!P) return P;
  const o = {};
  for (const [k, v] of Object.entries(P)) {
    if (k === "hips" && !Array.isArray(v)) { o.hips = v; continue; }
    if (k === "_hips") { o._hips = [-v[0], v[1], v[2]]; continue; }
    if (k === "_root") { o._root = { p: v.p ? [-v.p[0], v.p[1], v.p[2]] : undefined, r: v.r ? [v.r[0], -v.r[1], -v.r[2]] : undefined }; continue; }
    if (k === "_hands" || k === "_fingers" || k === "_snap") { o[k] = { L: v.R, R: v.L }; continue; }
    if (k === "_ik") { o._ik = {}; for (const [lk, g] of Object.entries(v)) o._ik[mirrorName(lk)] = { ...g, to: g.to && [mirrorName(g.to[0]), [-g.to[1][0], g.to[1][1], g.to[1][2]]], on: g.on && [mirrorName(g.on[0]), g.on[1], g.on[2] ? [-g.on[2][0], g.on[2][1], g.on[2][2]] : undefined], poleIn: g.poleIn && mirrorName(g.poleIn), pole: g.pole && [-g.pole[0], g.pole[1], g.pole[2]] }; continue; }
    if (k === "_aim") { o._aim = {}; for (const [lk, a] of Object.entries(v)) o._aim[mirrorName(lk)] = a && { ...a, fingers: [-a.fingers[0], a.fingers[1], a.fingers[2]], palm: [-a.palm[0], a.palm[1], a.palm[2]], space: a.space && a.space !== "model" ? mirrorName(a.space) : a.space }; continue; }
    if (k.startsWith("_")) { o[k] = v; continue; }
    o[mirrorName(k)] = [v[0], -(v[1] || 0), -(v[2] || 0)];
  }
  return o;
}


const ZERO3 = [0, 0, 0];

// ── the rig ──────────────────────────────────────────────────────────
export function makeRig(root, B, opts = {}) {
  let ieh = null, arm = null;
  root.traverse((o) => { if (!ieh && o.userData && o.userData.ieh) { ieh = o.userData.ieh; arm = o; } });
  const noop = { ok: false, begin() {}, apply() { return null; }, end() {}, play() {}, stop() {}, snap() {}, wear() {}, wearing() { return null; }, clip: null, ieh: null };
  if (!ieh) return noop;
  const hair = opts.hair !== false;

  // ── THE PARITY LAW: the FIRST touch of a frame saves the bone as the layers below us left
  // it; begin() puts exactly that back, so no layer ever builds on its own last frame ──
  const touched = new Map(), dirty = new Set();
  const touch = (b) => { if (dirty.has(b)) return; dirty.add(b); const q = touched.get(b); if (q) q.copy(b.quaternion); else touched.set(b, b.quaternion.clone()); };
  const touchedPos = new Map(), dirtyPos = new Set();
  const touchPos = (b) => { if (dirtyPos.has(b)) return; dirtyPos.add(b); const p = touchedPos.get(b); if (p) p.copy(b.position); else touchedPos.set(b, b.position.clone()); };
  let fresh = false, clavDone = false;       // the frame token: begin() → apply() → end()

  // THE REST SNAPSHOT: every bone's rest offset, taken ONCE (makeRig runs on a fresh, unposed
  // body; a grafted bone is at rest when first seen). Chains are rebuilt on a costume change —
  // mid-pose — and must measure her REST, not whatever the clip is doing (critic, 2026-09-27:
  // re-wearing mid-sit flipped every chain's tip upward and shortened four for good)
  const REST = new WeakMap();
  root.traverse((o) => { if (o.isBone) REST.set(o, o.position.clone()); });
  const restPos = (o) => { let p = REST.get(o); if (!p) { p = o.position.clone(); REST.set(o, p); } return p; };
  const restHead = (b, out = V()) => { out.set(0, 0, 0); let o = b; while (o && o.isBone) { out.add(restPos(o)); o = o.parent; } return out; };
  const worldQ = (o, out) => o.getWorldQuaternion(out);
  const scaleOf = () => { arm.getWorldScale(_v7); return _v7.x; };
  const floorY = () => { arm.getWorldPosition(_v7); return _v7.y + (ieh.floor || 0) * scaleOf(); };

  // ── drivers ──
  const drivers = (ieh.drivers || []).map((d) => ({
    ...d, b: B[d.bone], of: B[d.of],
    axis: d.axis ? new THREE.Vector3(...d.axis).normalize() : null,
    dir: d.dir ? new THREE.Vector3(...d.dir).normalize() : null,
  })).filter((d) => d.b && d.of);
  const clavs = drivers.filter((d) => d.kind === "clav");
  const posts = drivers.filter((d) => d.kind !== "clav");

  // ── colliders (capsules between two bones' heads, or two points in one bone) ──
  const cols = {};
  for (const c of ieh.colliders || []) {
    const a = B[c.a], b = B[c.b];
    if (!a || !b) continue;
    cols[c.name] = { a, b, pa: c.pa ? new THREE.Vector3(...c.pa) : null, pb: c.pb ? new THREE.Vector3(...c.pb) : null, r: c.r, A: V(), Bp: V() };
  }
  const colList = Object.values(cols);
  const extraCols = [];            // the scene's props (ctl.colliders): a chair seat, a bench
  let lastCols = null;

  // ── IK (arms and legs) ──
  const limbs = {
    L: ["upperarmL", "forearmL", "handL"], R: ["upperarmR", "forearmR", "handR"],
    footL: ["thighL", "shinL", "footL"], footR: ["thighR", "shinR", "footR"],
  };
  const LIMBS_LR = ["footL", "footR", "L", "R"], LIMBS_RL = ["footL", "footR", "R", "L"];
  const ikLast = { L: V(), R: V(), footL: V(), footR: V() };
  const limbRest = {};
  function restOf(k) {
    if (limbRest[k] !== undefined) return limbRest[k];
    const bs = (limbs[k] || []).map((n) => B[n]);
    if (bs.length !== 3 || bs.some((b) => !b)) return (limbRest[k] = null);
    const [a, b, c] = bs, ha = restHead(a), hb = restHead(b), hc = restHead(c);
    return (limbRest[k] = { a, b, c, L1: ha.distanceTo(hb), L2: hb.distanceTo(hc), d1: hb.clone().sub(ha).normalize(), d2: hc.clone().sub(hb).normalize() });
  }
  const _i1 = V(), _i2 = V(), _i3 = V(), _i4 = V(), _i5 = V(), _i6 = V(), _i7 = V();
  const _iq1 = Qn(), _iq2 = Qn(), _iq3 = Qn();
  // THE ELBOW IS A HINGE (2026-10-01 — "bone sticking out of her arm", the robot's arms-down pose). The two-bone solve below turns the
  // upper arm by the SHORTEST swing and never rolls it, so when the forearm has to bend in a plane her elbow can't hinge in (upper arm
  // out to the side, forearm hanging down: the hinge would have to be the world's front-back axis) the forearm took a 90 degree
  // SIDEWAYS bend, which her skin (painted for an x hinge) folds into a ledge. Roll the upper arm about its own axis so the hinge
  // (forearm local +x, flexion = a negative turn) lies across the bend. It only acts when the hinge is well off (60 -> 85 degrees,
  // smoothstep) AND only for a goal that asks for it (`hinge: 1` on the arm's _ik goal — the robot's), so every other pose is untouched to the bit; the elbow and wrist stay
  // exactly where they were. (First tried for every pose: sit_chair, think and the kneels rolled their upper arms 100+ degrees — a look change nobody asked for.)
  const _er1 = V(), _er2 = V(), _er3 = V(), _erq1 = Qn(), _erq2 = Qn();
  const sstep = (x) => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));
  function elbowRoll(a, up, lo, w) {
    _er1.crossVectors(up, lo);
    const sinB = _er1.length();
    if (sinB < 0.1) return;                                   // (nearly) straight: no bend plane to hinge in
    _er1.divideScalar(sinB).negate();                         // the hinge axis this bend wants (flexion turns about local +x, negatively)
    worldQ(a, _erq1);
    _er2.set(1, 0, 0).applyQuaternion(_erq1);                 // the upper arm's own hinge axis, in the world
    _er2.addScaledVector(up, -_er2.dot(up));
    const l2 = _er2.length(); if (l2 < 1e-4) return;
    _er2.divideScalar(l2);
    const cosD = Math.min(1, Math.max(-1, _er2.dot(_er1)));
    const g = sstep((Math.acos(cosD) - 1.05) / 0.44) * sstep((sinB - 0.1) / 0.25);
    if (g <= 0) return;
    _er3.crossVectors(_er2, _er1);
    _erq2.setFromAxisAngle(up, Math.atan2(_er3.dot(up), cosD) * g * w).multiply(_erq1);
    worldQ(a.parent, _erq1).invert();
    a.quaternion.copy(_erq1.multiply(_erq2));
    a.updateMatrixWorld(true);
  }
  // the end joint to `target` (world), the bend toward `pole` (world direction), weight w
  function solveIK(k, target, pole, w = 1, hinge = 0) {
    const R = restOf(k); if (!R) return;
    const { a, b, L1, L2, d1, d2 } = R;
    touch(a); touch(b);
    a.getWorldPosition(_i1);
    const s = scaleOf();
    _i2.copy(target).sub(_i1);
    let d = _i2.length() / s; _i2.normalize();
    d = Math.min(Math.max(d, Math.abs(L1 - L2) + 0.005), L1 + L2 - 0.002);
    const cosA = Math.min(1, Math.max(-1, (L1 * L1 + d * d - L2 * L2) / (2 * L1 * d)));
    const sinA = Math.sqrt(1 - cosA * cosA);
    _i3.copy(pole).addScaledVector(_i2, -pole.dot(_i2));
    if (_i3.lengthSq() < 1e-8) { _i3.set(0, 0, 1).applyQuaternion(worldQ(B.hips, _iq1)).addScaledVector(_i2, -_i2.z); }
    _i3.normalize();
    _i4.copy(_i2).multiplyScalar(cosA).addScaledVector(_i3, sinA).normalize();     // upper dir
    _i5.copy(_i1).addScaledVector(_i4, L1 * s);                                     // elbow / knee
    _i6.copy(_i1).addScaledVector(_i2, d * s).sub(_i5).normalize();               // lower dir
    worldQ(a, _iq1); _i7.copy(d1).applyQuaternion(_iq1);
    _iq2.setFromUnitVectors(_i7, _i4).multiply(_iq1);
    worldQ(a.parent, _iq3).invert();
    a.quaternion.slerp(_iq3.multiply(_iq2), w);
    a.updateMatrixWorld(true);
    if (hinge > 0 && (k === "L" || k === "R")) elbowRoll(a, _i4, _i6, w * hinge);
    worldQ(b, _iq1); _i7.copy(d2).applyQuaternion(_iq1);
    _iq2.setFromUnitVectors(_i7, _i6).multiply(_iq1);
    worldQ(b.parent, _iq3).invert();
    b.quaternion.slerp(_iq3.multiply(_iq2), w);
    b.updateMatrixWorld(true);
  }
  // AIM A HAND: { fingers: [x, y, z], palm: [x, y, z], space: "model" | bone } — where the
  // fingers point and the way the palm faces (at rest her fingers hang, palms to her midline)
  const _aF = V(), _aN = V(), _aC = V(), _am1 = new THREE.Matrix4(), _am2 = new THREE.Matrix4(), _aq = Qn(), _aq2 = Qn();
  const _aF2 = V(), _aN2 = V();
  function aimHand(k, a, w, b, e) {
    const hb = B["hand" + k]; if (!hb) return;
    touch(hb);
    worldQ(a.space && a.space !== "model" && B[a.space] ? B[a.space] : arm, _aq);
    _aF.set(a.fingers[0], a.fingers[1], a.fingers[2]).applyQuaternion(_aq).normalize();
    _aN.set(a.palm[0], a.palm[1], a.palm[2]).applyQuaternion(_aq);
    if (b) {                                         // between two aims: blend the two directions
      worldQ(b.space && b.space !== "model" && B[b.space] ? B[b.space] : arm, _aq);
      _aF2.set(b.fingers[0], b.fingers[1], b.fingers[2]).applyQuaternion(_aq).normalize();
      _aN2.set(b.palm[0], b.palm[1], b.palm[2]).applyQuaternion(_aq);
      _aF.lerp(_aF2, e); if (_aF.lengthSq() < 1e-6) _aF.copy(_aF2); _aF.normalize();
      _aN.normalize().lerp(_aN2.normalize(), e);
    }
    _aN.addScaledVector(_aF, -_aN.dot(_aF)).normalize();
    _aC.crossVectors(_aF, _aN);
    _am1.makeBasis(_aF, _aN, _aC);
    // the same two directions in the hand's own rest frame (identity rest = her model axes)
    _aF.set(0, -1, 0); _aN.set(k === "L" ? -1 : 1, 0, 0); _aC.crossVectors(_aF, _aN);
    _am2.makeBasis(_aF, _aN, _aC).transpose();
    _aq.setFromRotationMatrix(_am1.multiply(_am2));                     // the hand, world
    worldQ(hb.parent, _aq2).invert();
    hb.quaternion.slerp(_aq2.multiply(_aq), w);
    hb.updateMatrixWorld(true);
  }
  const _tg = V(), _pl = V(), _tg2 = V(), _pl2 = V();
  // where a hand's fingers will point this frame (its aim, blended like aimHand) — world, unit
  const _fw1 = V(), _fw2 = V(), _fwq = Qn();
  function fingersOne(x, v) { worldQ(x.space && x.space !== "model" && B[x.space] ? B[x.space] : arm, _fwq); return v.set(x.fingers[0], x.fingers[1], x.fingers[2]).applyQuaternion(_fwq).normalize(); }
  function fingersWorld(k, out, dst) {
    const a = out.aimA && out.aimA[k], b = out.aimB && out.aimB[k];
    if (a && b && a !== b) { fingersOne(a, dst); fingersOne(b, _fw1); dst.lerp(_fw1, out.e); if (dst.lengthSq() < 1e-8) dst.copy(_fw1); return dst.normalize(); }
    if (a || b) return fingersOne(a || b, dst);
    return null;
  }
  const _ff = V(), _ffu = new THREE.Vector3(0, 1, 0), _ffa = Qn(), _ffb = Qn(), _ffc = Qn();
  function flatFoot(fb, w) {
    if (!fb || w <= 0) return;
    touch(fb);
    fb.updateMatrixWorld(true);
    worldQ(arm, _ffb);                                   // her model's frame: "level" is its floor
    worldQ(fb, _ffa);
    _ff.set(0, 0, 1).applyQuaternion(_ffa).applyQuaternion(_ffc.copy(_ffb).invert());   // the foot's heading, in model space
    const yaw = Math.atan2(_ff.x, _ff.z);
    _ffc.setFromAxisAngle(_ffu, yaw);
    _ffb.multiply(_ffc);                                  // level, turned to that heading
    worldQ(fb.parent, _ffc).invert();
    fb.quaternion.slerp(_ffc.multiply(_ffb), w);
    fb.updateMatrixWorld(true);
  }
  // the pole is in MODEL space — or in a bone's (poleIn: "chest": the elbows turn with her torso)
  const poleOf = (g, out) => { const pl = g.pole || ZERO3; const sp = g.poleIn && B[g.poleIn]; return out.set(pl[0], pl[1], pl[2] || (pl[0] || pl[1] ? 0 : 1)).transformDirection(sp ? sp.matrixWorld : arm.matrixWorld); };
  // ON A PART (any humanoid): { on: [bone, u, [x, y, z]] } = u of the way along that bone (to its
  // first child: a thigh's u 0.7 is most of the way to the knee), then [x, y, z] metres in the
  // bone's own frame (identity rest = her model axes: +x her left, +y up, +z front). The goal sits
  // a few cm INSIDE the part, and the hand is snapped out onto its surface by the arm clearance, so
  // the same numbers put a hand ON a slim thigh or a thick one.
  // (a midline bone's child ON the midline first — the chest's neck, not a clavicle — whatever order
  // the glb lists them in; a sided bone's first child)
  const SIDED = /[LR]\d*$/;
  const childOf = (bn) => {
    let first = null;
    for (const c of bn.children) {
      if (!c.isBone || /Twist|^skirt_|^dread|^tail/.test(c.name)) continue;
      if (!SIDED.test(bn.name) && !SIDED.test(c.name)) return c;
      if (!first) first = c;
    }
    return first;
  };
  // ── MOVES FIT THE BODY (body pass 2, 2026-09-30). A move is written in the metres of the body it was
  // authored on (V'ael): hips drop 0.385 to a seat, a guard's fists at chin height, a stance 0.4 m long.
  // A FORMED body (ieForm.js) carries that reference (ieh.ref); the rig measures its own and rescales:
  // hip offsets and floor/stance goals × the LEG ratio, hand goals in model space ride her CHEST (longer
  // legs lift the guard with her), whole-body root offsets (lying, the worm) × the HEIGHT ratio. A body
  // with no ieh.ref (every unformed one) gets exactly 1 / 0: the moves play as they always did.
  const FIT = (() => {
    const r = ieh.ref, f = { L: 1, H: 1, chest: new THREE.Vector3() };
    if (!r || !B.thighL || !B.shinL || !B.footL || !B.thighR || !B.shinR || !B.footR) return f;
    const leg = (k) => restHead(B["thigh" + k]).distanceTo(restHead(B["shin" + k])) + restHead(B["shin" + k]).distanceTo(restHead(B["foot" + k]));
    if (r.leg > 0) f.L = 0.5 * (leg("L") + leg("R")) / r.leg;
    if (r.top > 0 && ieh.model_top) f.H = ieh.model_top / r.top;
    if (r.chest && B.chest) f.chest.copy(restHead(B.chest)).sub(new THREE.Vector3(r.chest[0], r.chest[1], r.chest[2]));
    return f;
  })();
  function goalWorld(g, out, k) {
    if (g.on) {
      const bn = B[g.on[0]]; if (!bn) return null;
      const c = childOf(bn), u = g.on[1] || 0, o = g.on[2] || ZERO3;
      if (c) out.copy(restPos(c)).multiplyScalar(u); else out.set(0, 0.2 * u, 0);
      out.x += o[0]; out.y += o[1]; out.z += o[2];
      return bn.localToWorld(out);
    }
    const sp = g.to[0], p = g.to[1];
    out.set(p[0], p[1], p[2]);
    if (sp === "model" || sp === "floor") {                         // (MOVES FIT THE BODY: see FIT)
      if (k === "footL" || k === "footR") { out.x *= FIT.L; out.z *= FIT.L; if (sp === "model") out.y *= FIT.L; }
      else if (sp === "model") out.add(FIT.chest);
    }
    const ly = out.y;                                                // (the goal's own height, BEFORE it goes to world: a "floor" goal's y is metres above her floor)
    if (sp === "model" || sp === "floor") arm.localToWorld(out);
    else { const bn = B[sp]; if (!bn) return null; bn.localToWorld(out); }
    if (sp === "floor") out.y = floorY() + ly * scaleOf();
    return out;
  }

  // ── THE SHOULDERS (auto clavicle): past ~60° of raise the shoulder lifts too. The hand
  // STAYS where the layers below put it: the arm re-bends to reach the same wrist point ──
  const _c1 = V(), _c2 = V(), _c3 = V(), _cq = Qn(), _cq2 = Qn(), _cqh = Qn();
  function driveClavicles() {
    if (clavDone) return;
    clavDone = true;
    for (const d of clavs) {
      _v1.copy(d.dir).applyQuaternion(d.of.quaternion);
      const el = Math.acos(Math.max(-1, Math.min(1, -_v1.y)));
      const a = Math.min(d.max !== undefined ? d.max : 0.45, Math.max(0, el - (d.e0 !== undefined ? d.e0 : 1.05)) * (d.k !== undefined ? d.k : 0.35));
      if (a < 1e-5) continue;
      const k = d.side > 0 ? "L" : "R", R = restOf(k);
      if (R) { R.c.getWorldPosition(_c1); R.b.getWorldPosition(_c2); R.a.getWorldPosition(_c3); R.c.getWorldQuaternion(_cqh); touch(R.c); }
      const hx = Math.abs(_v1.x), hz = Math.max(0, _v1.z), tot = hx + Math.abs(_v1.z) + 1e-9;
      const wl = hx + Math.abs(_v1.z) > 1e-6 ? hx / tot : 1, wf = hz / tot;
      _cq.setFromEuler(_e.set(0, -d.side * a * wf * 0.8, d.side * a * wl, "XYZ"));
      touch(d.b); touch(d.of);
      d.b.quaternion.multiply(_cq);
      d.of.quaternion.premultiply(_cq2.copy(_cq).invert());
      d.b.updateMatrixWorld(true);
      if (R) {                                                  // keep the hand where it was
        _c3.add(_c1).multiplyScalar(0.5);
        _pl.subVectors(_c2, _c3);                               // the old bend plane
        solveIK(k, _c1, _pl, 1);
        // …and the hand keeps its world turn (re-bending the arm alone turned it 8-12° —
        // the final critic, 2026-09-27)
        R.c.parent.getWorldQuaternion(_cq2).invert();
        R.c.quaternion.copy(_cq2.multiply(_cqh));
        R.c.updateMatrixWorld(true);
      }
    }
  }

  // ── THE SHOULDERS, PART 2 — POSE-SPACE CORRECTIVES (2026-09-30, caP'n: "all twisted up … work correctly like a real shoulder").
  // Linear blend skinning flattens a raised shoulder (the deltoid collapses, the armpit webs into a crumpled flap). tools/rigup/psd_build.py
  // re-solves the shoulder band of every mesh (skin AND garments) with ARAP at a grid of arm directions and stores the difference as
  // SPARSE morph targets (mesh.extras.ieh_psd = { sigma, rest:{L,R}, samples:[{side, d}] }, target i ↔ sample i). Here: the arm's
  // direction in the chest frame → a Gaussian kernel per sample (the REST direction is an anchor of zero correction, so a hanging arm
  // is byte-identical to the old body) → the target influences, on the mesh AND its ink hull. Undriven, every influence is 0.
  // Nothing allocates per frame; a rig error turns only this layer off.
  const PSD = { list: [], spec: new Map(), dirty: true, ok: true, dL: V(), dR: V() };
  const _pa1 = V(), _pa2 = V();
  function psdScan() {
    PSD.list.length = 0; PSD.spec.clear();
    root.traverse((o) => {
      if (!o.isMesh || !o.morphTargetInfluences || !o.morphTargetInfluences.length) return;
      let sp = o.userData && o.userData.ieh_psd;
      if (!sp && /_ink$/.test(o.name) && o.parent) { const src = o.parent.getObjectByName(o.name.slice(0, -4)); sp = src && src.userData && src.userData.ieh_psd; }
      if (!sp || sp.v !== 2 || sp.targets.length !== o.morphTargetInfluences.length) return;
      if (!sp._d) {                                   // once per spec: direction vectors + the target index lists per side
        sp._d = sp.samples.map((q) => new THREE.Vector3().fromArray(q.d));
        sp._r = { L: new THREE.Vector3().fromArray(sp.rest.L), R: new THREE.Vector3().fromArray(sp.rest.R) };
        sp._t = { L: [], R: [] }; sp.targets.forEach((sd, i) => sp._t[sd].push(i));
        sp._acc = { L: new Float32Array(sp._t.L.length), R: new Float32Array(sp._t.R.length) };
      }
      PSD.list.push(o); PSD.spec.set(o, sp);
    });
    PSD.dirty = false;
  }
  function driveShoulders() {
    if (!PSD.ok || opts.shoulders === false) return;
    try {
      if (PSD.dirty) psdScan();
      if (!PSD.list.length || !B.chest) return;
      B.chest.updateWorldMatrix(true, false);
      for (const sd of SIDES2) {
        const a = B["upperarm" + sd], f = B["forearm" + sd];
        if (!a || !f) continue;
        a.getWorldPosition(_pa1); f.getWorldPosition(_pa2);
        B.chest.worldToLocal(_pa1); B.chest.worldToLocal(_pa2);
        (sd === "L" ? PSD.dL : PSD.dR).subVectors(_pa2, _pa1).normalize();
      }
      for (const m of PSD.list) {
        const sp = PSD.spec.get(m), inf = m.morphTargetInfluences, sg = 1 / (sp.sigma * sp.sigma);
        for (const sd of SIDES2) {
          const dd = sd === "L" ? PSD.dL : PSD.dR, acc = sp._acc[sd], tix = sp._t[sd];
          if (!tix.length) continue;
          const ang0 = Math.acos(Math.max(-1, Math.min(1, dd.dot(sp._r[sd]))));
          let tot = Math.exp(-ang0 * ang0 * sg);       // the REST anchor: a coefficient vector of zeros
          acc.fill(0);
          for (let i = 0; i < sp.samples.length; i++) {
            const q = sp.samples[i]; if (q.side !== sd) continue;
            const an = Math.acos(Math.max(-1, Math.min(1, dd.dot(sp._d[i])))), w = Math.exp(-an * an * sg);
            if (w < 1e-4) continue;
            tot += w; for (let k = 0; k < acc.length; k++) acc[k] += w * q.c[k];
          }
          for (let k = 0; k < tix.length; k++) inf[tix[k]] = acc[k] / tot;
        }
      }
    } catch (e) { PSD.ok = false; console.warn("[ieRig] shoulder correctives off:", e); }
  }

  const thighs = { L: B.thighL, R: B.thighR };

  // segment p0-p1 vs capsule A-B: distance, and the normal (from the capsule axis to the segment)
  const SC = { d: 0, s: 0 };
  const _s1 = V(), _s2 = V(), _s3 = V(), _sa = V(), _sb = V();
  function segCapsule(p0, p1, A, Bc, out) {
    _s1.subVectors(p1, p0); _s2.subVectors(Bc, A); _s3.subVectors(p0, A);
    const a = _s1.dot(_s1), e = _s2.dot(_s2), f = _s2.dot(_s3);
    let sN, tN;
    if (a <= 1e-9 && e <= 1e-9) { sN = 0; tN = 0; }
    else if (a <= 1e-9) { sN = 0; tN = Math.min(1, Math.max(0, f / e)); }
    else {
      const c = _s1.dot(_s3);
      if (e <= 1e-9) { tN = 0; sN = Math.min(1, Math.max(0, -c / a)); }
      else {
        const b = _s1.dot(_s2), den = a * e - b * b;
        sN = den > 1e-9 ? Math.min(1, Math.max(0, (b * f - c * e) / den)) : 0;
        tN = (b * sN + f) / e;
        if (tN < 0) { tN = 0; sN = Math.min(1, Math.max(0, -c / a)); }
        else if (tN > 1) { tN = 1; sN = Math.min(1, Math.max(0, (b - c) / a)); }
      }
    }
    _sa.copy(p0).addScaledVector(_s1, sN);
    _sb.copy(A).addScaledVector(_s2, tN);
    out.subVectors(_sa, _sb);
    const d = out.length();
    if (d > 1e-6) out.divideScalar(d); else out.set(0, 0, 1);
    SC.d = d; SC.s = sN;
    return SC;
  }
  const _r1 = V(), _r2 = V(), _rn = V();
  const inwardOf = (h0, tip, F) => {
    const zc = (F.ringGeo && F.ringGeo.zc) || 0;
    const v = new THREE.Vector3(-(h0.x + tip.x) / 2, 0, -((h0.z + tip.z) / 2 - zc));
    return v.lengthSq() > 1e-10 ? v.normalize() : v.set(0, 0, 0);
  };
  // ── chains: rebuilt whenever her clothes change (rig.wear) — a costume change is a cut ──
  const chains = [], famList = [];
  // every ring: used = what any worn garment rides on; pairs = every garment's joins; inset = the
  // deepest any worn garment's cloth hangs inside each chain's line (per segment)
  function ringUnion() {
    for (const F of Object.values(ieh.chains || {})) {
      if (!F.worn) continue;
      const used = new Set(), pairs = new Map(), inset = {};
      for (const g of Object.values(F.worn)) {
        for (const c of g.used || []) used.add(c);
        for (const pr of g.pairs || []) pairs.set(pr[0] < pr[1] ? pr[0] + "|" + pr[1] : pr[1] + "|" + pr[0], pr);
        for (const [c, a] of Object.entries(g.inset || {})) {
          const o = inset[c] || [];
          inset[c] = Array.from({ length: Math.max(o.length, a.length) }, (_, k) => Math.max(o[k] || 0, a[k] || 0));
        }
      }
      for (const [cn, C] of Object.entries(F.chains || {})) if (!C.from) { C.used = used.has(cn); C.inset = inset[cn] || null; }
      F.pairs = [...pairs.values()];
    }
  }
  ringUnion();
  function buildChains() {
    for (const ch of chains) for (const b of ch.bones) if (b.userData.rigChain === ch.fam) delete b.userData.rigChain;
    chains.length = 0; famList.length = 0;
    for (const [fam, F] of Object.entries(ieh.chains || {})) {
      if (F.kind === "hair" && !hair && !F.from) continue;     // (her own hair: acting bodies only; a worn bow always)
      for (const [cname, C] of Object.entries(F.chains || {})) {
        if (C.used === false) continue;              // a ring chain no garment she wears rides on
        const bones = C.joints.map((n) => B[n]);
        if (bones.some((b) => !b)) { if (C.from) console.warn("[ieRig] " + C.from + ": chain " + cname + " names a bone this body lacks"); continue; }
        for (const b of bones) b.userData.rigChain = fam;      // other layers leave these to us
        const n = bones.length;
        const heads = bones.map((b) => restHead(b));
        const tip = new THREE.Vector3(...C.tip);
        const lens = [];
        for (let i = 0; i < n; i++) lens.push((i < n - 1 ? heads[i + 1] : tip).distanceTo(heads[i]));
        chains.push({
          fam, kind: F.kind, name: cname, F, C, bones, n, lens, heads, tipRest: tip,
          tipLocal: tip.clone().sub(heads[n - 1]),
          P: Array.from({ length: n + 1 }, V), A: Array.from({ length: n + 1 }, V),
          D: Array.from({ length: n }, V), W: Array.from({ length: n }, V),
          vel: V(), last: V(), init: false,
          cl: new Float32Array(n),                                   // this frame's segment lengths (cloth may BUNCH)
          loc: bones.map((b) => restPos(b)),                          // each joint's rest offset from its parent
          side: C.side || 0, u: C.u || 0, width: C.width || null,
          // the cloth's offset from the chain line: inward (toward the ring's axis) at rest, turned
          // with each segment — see clothOff()
          inset: C.inset || null, inw: inwardOf(heads[0], tip, F), rdir: heads.concat([tip]).slice(1).map((p, k) => p.clone().sub(heads[k]).normalize()),
          front: C.front !== undefined ? !!C.front : (C.side || 0) === 0 && (C.az === undefined || Math.abs(C.az) < 90),
          followK: C.follow !== undefined ? C.follow : (C.side || 0) === 0 ? 1.3 : 1.15 - 0.75 * (C.u || 0),
        });
      }
    }
    // CALIBRATE AT REST: a collider that already touches a chain in her rest pose (the seat
    // capsule round her butt vs the back panels lying on it) would shove the cloth off her for
    // ever. Each segment gets its own clearance per collider: radius + margin (+ the panel's
    // half-width, so its EDGES stay off a leg too), never more than the rest pose leaves free.
    for (const ch of chains) {
      const pts = ch.heads.concat([ch.tipRest]);
      const margin = ch.F.margin || 0.015;
      ch.clear = [];
      for (let i = 0; i < ch.n; i++) {
        const row = {};
        for (const cn of ch.F.collide || []) {
          const c = cols[cn]; if (!c) continue;
          restHead(c.a, _r1); if (c.pa) _r1.add(c.pa);
          restHead(c.b, _r2); if (c.pb) _r2.add(c.pb);
          const d = segCapsule(pts[i], pts[i + 1], _r1, _r2, _rn).d;
          const wdt = ch.width ? Math.max(ch.width[i] || 0, ch.width[i + 1] || 0) : 0;
          row[cn] = Math.max(0.004, Math.min(c.r + margin + wdt, d - 0.004));
        }
        ch.clear.push(row);
      }
    }
    // families: a panel = one side's chains in order across it (for the ring pass)
    {
      const fams = {};
      for (const ch of chains) (fams[ch.fam] || (fams[ch.fam] = { F: ch.F, list: [] })).list.push(ch);
      for (const fam of Object.values(fams)) {
        const by = {};
        for (const ch of fam.list) (by[ch.side] || (by[ch.side] = [])).push(ch);
        fam.groups = Object.values(by).map((g) => g.sort((a, b) => a.u - b.u)).filter((g) => g.length > 1);
        // the neighbours whose cloth is joined: from the garments she wears (the ring), or by side
        const byName = {};
        for (const ch of fam.list) byName[ch.name] = ch;
        const list = fam.F.pairs ? fam.F.pairs.map(([a, b]) => [byName[a], byName[b]]).filter(([a, b]) => a && b)
          : [].concat(...fam.groups.map((g) => g.slice(0, -1).map((a, i) => [a, g[i + 1]])));
        if (fam.F.pairs) fam.groups = [fam.list];
        fam.pairs = [];
        for (const [a, b] of list) {
          const rd = [0];
          for (let i = 1; i <= Math.min(a.n, b.n); i++) {          // (a garment may join chains of unequal length)
            const pa = i < a.n ? a.heads[i] : a.tipRest, pb = i < b.n ? b.heads[i] : b.tipRest;
            rd.push(pa.distanceTo(pb));
          }
          fam.pairs.push({ a, b, rd });
        }
        famList.push(fam);
      }
    }
  }
  buildChains();

  // ── THE WARDROBE (caP'n, 2026-09-27: "make sure we don't have to redo the rig if we change
  // clothes"). The ring round her hips is HERS, not the skirt's: a garment only says which ring
  // chains its cloth rides on (`used`) and which neighbours its cloth joins (`pairs`), and may
  // bring chains of its own (a ponytail, a cape: `chains`, on bones attachPart grafted on).
  //   rig.wear(name, iehWear)  puts one on;  rig.wear(name, null)  takes it off
  //   rig.wearing(name)        what a worn garment says (to put it back later)
  // iehWear = { garment, <family>: { used, pairs }, chains: { <family>: { kind, chains, ... } } }
  function wearing(name) {
    const out = { garment: name };
    let any = false;
    for (const [fam, F] of Object.entries(ieh.chains || {})) {
      if (F.worn && F.worn[name]) { out[fam] = F.worn[name]; any = true; }
      const own = {};
      for (const [cn, C] of Object.entries(F.chains || {})) if (C.from === name) own[cn] = C;
      if (Object.keys(own).length) { (out.chains || (out.chains = {}))[fam] = { ...F, chains: own, worn: undefined }; any = true; }
    }
    return any ? out : null;
  }
  function wear(name, info) {
    const fams = ieh.chains || (ieh.chains = {});
    // 1. off: the garment's own chains, and its say in the ring
    for (const [fam, F] of Object.entries(fams)) {
      for (const [cn, C] of Object.entries(F.chains || {})) if (C.from === name) delete F.chains[cn];
      if (F.from && !Object.keys(F.chains || {}).length) delete fams[fam];     // a garment's family, now empty
      else if (F.worn) delete F.worn[name];
    }
    // 2. on: its own chains (a family it brings, or more chains in one she has)…
    if (info) {
      for (const [fam, G] of Object.entries(info.chains || {})) {
        const F = fams[fam] || (fams[fam] = { ...G, chains: {}, from: name });
        for (const [cn, C] of Object.entries(G.chains || {})) {
          // a name she already has is HERS: never overwritten (taking the garment off would delete it)
          if (F.chains[cn] && F.chains[cn].from !== name) { console.warn("[ieRig] " + name + ": chain " + cn + " clashes with one she has — skipped"); continue; }
          F.chains[cn] = { ...C, from: name };
        }
      }
      // …and which ring chains it rides on
      for (const [fam, F] of Object.entries(fams)) if (F.worn && info[fam]) F.worn[name] = { used: info[fam].used || [], pairs: info[fam].pairs || [], inset: info[fam].inset || {} };
    }
    ringUnion();
    buildChains();
    CLR.dirty = true;                 // the arm clearance re-measures what she has on
  }

  // floor-contact points: [joint index, x, y, z] from the joint's head
  const skinBones = (() => { let s = null; root.traverse((o) => { if (!s && o.isSkinnedMesh) s = o.skeleton.bones; }); return s; })();
  const contacts = (ieh.contacts || []).map((c) => ({ b: skinBones && skinBones[c[0]], p: new THREE.Vector3(c[1], c[2], c[3]) })).filter((c) => c.b);
  function lowestContact() {
    let lo = Infinity;
    for (const c of contacts) { _v1.copy(c.p); c.b.localToWorld(_v1); if (_v1.y < lo) lo = _v1.y; }
    return lo;
  }

  // ── the clip player ──
  const lastQ = new Map(), lastHips = V();                  // the clip layer's last result (for crossfades)
  const xf = { on: false, k: 0, q: new Map(), pool: new Map(), last: new Map(), hips: V() };
  const _xq = Qn(), _xv = V();
  const player = { clip: null, t: 0, life: 0, travel: 0, w: 0, fadeIn: 0.25, fadeOut: 0.25, speed: 1, loop: false, stopping: false, key: null, done: false };
  const cur = {}, qa = {}, qb = {};
  const boneCache = new WeakMap();           // clip → the bones THIS body has
  function clipBones(clip) {
    let l = boneCache.get(clip);
    if (l) return l;
    const s = new Set();
    const add = (P) => { if (P) for (const k of Object.keys(P)) if (!k.startsWith("_") && B[k]) s.add(k); };
    add(clip.base);
    for (const k of clip.keys || []) add(k.pose);
    if (clip.gen) add(clip.gen(0, clip));
    if (clip.add) add(clip.add(0, clip));
    l = [...s];
    boneCache.set(clip, l);
    return l;
  }
  let warned = null;
  function play(clip, o = {}) {
    if (clip && !(clip.gen || (clip.keys && clip.keys.length))) {
      if (warned !== clip) { warned = clip; console.warn("[ieRig] not a clip (use MOVES.name, not a string):", clip); }
      clip = null;
    }
    if (!clip) { stop(o.fade); return; }
    if (player.clip === clip && !player.stopping && !o.restart) return;
    if (player.clip && player.w > 0 && lastQ.size) {
      xf.q.clear(); for (const [b, q] of lastQ) xf.q.set(b, (xf.pool.get(b) || xf.pool.set(b, Qn()).get(b)).copy(q));
      xf.hips.copy(lastHips); xf.on = true; xf.k = 0;
    } else xf.on = false;
    player.clip = clip; player.t = o.t0 || 0; player.speed = o.speed || clip.speed || 1;
    player.loop = o.loop !== undefined ? o.loop : !!clip.loop;
    player.fadeIn = o.fade !== undefined ? o.fade : (clip.fade !== undefined ? clip.fade : 0.25);
    player.fadeOut = clip.fadeOut !== undefined ? clip.fadeOut : player.fadeIn;
    player.stopping = false; player.done = false; player.life = player.t;
    if (xf.on) player.w = 1;
    player.travel = o.travel !== undefined ? o.travel : clip.travel || 0;
    evalOut.hands = evalOut.face = evalOut.props = null;
  }
  function stop(fade) {
    if (!player.clip) return;
    player.stopping = true;
    if (fade !== undefined) player.fadeOut = fade;
  }
  const evalOut = { hips: V(), root: false, rootR: V(), rootP: V(), hands: null, face: null, ikA: null, ikB: null, aimA: null, aimB: null, e: 0, ground: null, props: null, fingers: null };
  // t = the clip's time (held at dur by a held clip); life = a clock that never stops, for the
  // life layer (a held sit still breathes — the critic caught held clips frozen dead)
  let lifeFingers = null;
  function evalClip(clip, t, life) {
    let A, Bk, e = 0;
    if (clip.gen) { A = clip.gen(t, clip); Bk = A; }
    else {
      const K = clip.keys;
      let i = 0;
      while (i < K.length - 1 && K[i + 1].t <= t) i++;
      const ka = K[i], kb = K[Math.min(i + 1, K.length - 1)];
      A = ka.full || (ka.full = { ...(clip.base || {}), ...(ka.pose || {}) });
      Bk = kb.full || (kb.full = { ...(clip.base || {}), ...(kb.pose || {}) });
      const span = kb.t - ka.t;
      const u = span > 1e-6 ? Math.min(1, Math.max(0, (t - ka.t) / span)) : 1;
      e = (EASE[kb.ease || clip.ease || "io"] || EASE.io)(u);
      evalOut.hands = (u >= 0.5 ? kb.hands : ka.hands) || ka.hands || evalOut.hands;
      evalOut.face = (u >= 0.35 ? kb.face : ka.face) || ka.face || evalOut.face;
      evalOut.props = (u >= 0.5 ? kb.props : ka.props) || ka.props || evalOut.props;
    }
    for (const n of clipBones(clip)) {
      const a = A[n] || ZERO3, b = Bk[n] || ZERO3;
      qDeg(a, qa[n] || (qa[n] = Qn()));
      if (a === b) { (cur[n] || (cur[n] = Qn())).copy(qa[n]); continue; }
      qDeg(b, qb[n] || (qb[n] = Qn()));
      (cur[n] || (cur[n] = Qn())).slerpQuaternions(qa[n], qb[n], e);
    }
    // THE LIFE LAYER: clip.add(t) → a small pose (degrees) turned ON TOP of the keys, so a held
    // sit or a guard still breathes and bobs without keying every breath
    if (clip.add) {
      const L = clip.add(life !== undefined ? life : t, clip);
      for (const n in L) {
        if (n[0] === "_" || !cur[n]) continue;
        cur[n].multiply(qDeg(L[n], _q3));
      }
      if (L._hips) { const lh = L._hips; _v2.set(lh[0], lh[1], lh[2]); } else _v2.set(0, 0, 0);
      lifeFingers = L._fingers || null;
    } else { _v2.set(0, 0, 0); lifeFingers = null; }
    const ha = A._hips || ZERO3, hb = Bk._hips || ZERO3;
    evalOut.hips.set(ha[0] + (hb[0] - ha[0]) * e, ha[1] + (hb[1] - ha[1]) * e, ha[2] + (hb[2] - ha[2]) * e).add(_v2);
    const ra = A._root, rb = Bk._root;
    if (ra || rb) {
      const pa = (ra && ra.p) || ZERO3, pb = (rb && rb.p) || ZERO3, ta = (ra && ra.r) || ZERO3, tb = (rb && rb.r) || ZERO3;
      evalOut.rootP.set(pa[0] + (pb[0] - pa[0]) * e, pa[1] + (pb[1] - pa[1]) * e, pa[2] + (pb[2] - pa[2]) * e);
      evalOut.rootR.set(ta[0] + (tb[0] - ta[0]) * e, ta[1] + (tb[1] - ta[1]) * e, ta[2] + (tb[2] - ta[2]) * e);
      evalOut.root = true;
    } else evalOut.root = false;
    if (clip.gen) { evalOut.hands = A._hands || null; evalOut.face = A._face || null; evalOut.props = A._props || null; evalOut.fingers = A._fingers || null; }
    else evalOut.fingers = (e < 0.5 ? A._fingers : Bk._fingers) || null;
    if (lifeFingers) evalOut.fingers = lifeFingers;
    // IK goals and hand aims BLEND between two keys, limb by limb (switching at the ease's
    // midpoint teleported every punch — critic, 2026-09-27); a limb only one key has fades in/out
    evalOut.ikA = A._ik || null; evalOut.ikB = Bk._ik || null;
    evalOut.aimA = A._aim || null; evalOut.aimB = Bk._aim || null;
    evalOut.e = A === Bk ? 0 : e;
    evalOut.ground = clip.ground || A._ground || null;
    return evalOut;
  }
  const applyOut = { w: 0, hands: null, face: null, props: null, fingers: null, t: 0, done: false };
  function groundHips(w) {
    root.updateMatrixWorld(true);
    const lo = lowestContact(), f = floorY();
    if (!isFinite(lo)) return;
    B.hips.getWorldPosition(_g0); _g1.copy(_g0); _g1.y += (f - lo) * w;
    B.hips.parent.worldToLocal(_g0); B.hips.parent.worldToLocal(_g1);
    B.hips.position.add(_g1.sub(_g0));
    root.updateMatrixWorld(true);
  }
  const _g0 = V(), _g1 = V();
  // THE SEAT (body pass 2, MOVES FIT THE BODY): a clip with `seat: true` (sit_chair, stand_up, piano) on a
  // FORMED body with a seat box in the scene (ctl.colliders) lands her seat ON it — a long-legged body with
  // level thighs hovered 17 cm over V'ael's chair — then re-bends each leg so the feet stay where they were
  // (knees rise, as anyone's do on a low chair). It fades out 10-18 cm off the seat (standing up is never
  // pulled back down). Unformed bodies sit exactly as the move was authored (no ieh.ref: skipped).
  const _sf = [V(), V()], _sp = V();
  function seatFit(w) {
    const c = cols.seat; if (!ieh.ref || !c || !c.pa || !extraCols.length) return;
    root.updateMatrixWorld(true);
    const s = scaleOf();
    c.A.copy(c.pa); c.a.localToWorld(c.A); c.Bp.copy(c.pb); c.b.localToWorld(c.Bp);
    const bottom = Math.min(c.A.y, c.Bp.y) - c.r * s, mx = 0.5 * (c.A.x + c.Bp.x), mz = 0.5 * (c.A.z + c.Bp.z);
    let top = -Infinity;
    for (const e of extraCols) if (e.box && mx >= e.box.min.x && mx <= e.box.max.x && mz >= e.box.min.z && mz <= e.box.max.z && e.box.max.y < bottom + 0.3 * s) top = Math.max(top, e.box.max.y);
    if (!isFinite(top)) return;
    const gap = bottom - top, k = w * (1 - ramp01(gap / s, 0.10, 0.18));
    if (k <= 0) return;
    for (let i = 0; i < 2; i++) { const fb = B[i ? "footR" : "footL"]; if (fb) fb.getWorldPosition(_sf[i]); }
    B.hips.getWorldPosition(_g0); _g1.copy(_g0); _g1.y -= gap * k;
    B.hips.parent.worldToLocal(_g0); B.hips.parent.worldToLocal(_g1);
    touchPos(B.hips); B.hips.position.add(_g1.sub(_g0));
    root.updateMatrixWorld(true);
    _sp.set(0, 0, 1).transformDirection(arm.matrixWorld);          // knees forward
    for (let i = 0; i < 2; i++) { const kk = i ? "footR" : "footL"; if (!B[kk]) continue; solveIK(kk, _sf[i], _sp, k); flatFoot(B[kk], k); }
    root.updateMatrixWorld(true);
  }

  // ── ARM CLEARANCE (pose-refinement session, 2026-09-27) ─────────────────────────────────
  // caP'n: "there's still some major arm clipping … making other poses should be literally ez pz".
  // Every frame a clip plays, her arms are kept OUT of her own body: each arm's surface points
  // (sampled off her real skinned meshes — skin AND whatever she wears) are tested against her
  // body's surface points (torso, legs, head, horns, the hair on her head), and a point that went
  // in pushes the elbow and the wrist back out onto the surface; the arm re-solves (lengths kept)
  // and the hand keeps its world turn (its aim). So a pose only has to put a hand NEAR where it
  // goes: a hand aimed at her thigh lands ON it, a forearm across her belly lies ON it.
  //   · `snap` on an IK goal (0-1, or true): the hand is also pulled ONTO the nearest surface
  //     within 10 cm (a hand that should rest on a knee never floats beside it)
  //   · measured from the meshes she is wearing NOW: rebuilt lazily after any costume change
  //     (rig.wear, attachPart) — changing clothes never re-rigs her, and never re-tunes this
  //   · points that already touch at rest (the armpit seam) are calibrated out, per body bone
  //   · a hand IK-planted on the FLOOR keeps its wrist (only the elbow moves)
  //   · off: makeRig(…, { clearance: false }), ctl.clear = false, or clip.clear = false
  // Works on any humanoid with the canon arm joints; a body without skinned meshes skips it.
  const CLR = { dirty: true, ok: false, M: 0.004, R: 0.06, SNAP: 0.10, last: { L: { pen: 0, arm: "", body: "" }, R: { pen: 0, arm: "", body: "" } } };
  let lastCtl = null;
  const ARM_J = /^(upperarm|upperarmTwist|forearm|forearmTwist|hand)([LR])$|^(index|middle|ring|pinky|thumb)([LR])\d$/;
  const chainKind = {};                            // joint name → its chain family's kind (skirt, hair, tail…)
  const kindsOf = () => { for (const F of Object.values(ieh.chains || {})) for (const C of Object.values(F.chains || {})) for (const j of C.joints || []) chainKind[j] = F.kind; };
  const SKIP_J = /^(skirt_|dread|tail)/;
  const _cm = new THREE.Matrix4(), _cn = new THREE.Matrix3();
  // a GARMENT (a COVER): most of it lies within 3 cm of her skin — cloth over her. Its normals are
  // turned away from the bone's axis (a dress lining's inner face points IN, and read every forearm
  // crossing the cutout's edge as "3 cm inside"), while SOLID meshes (the skin, horns) keep their own
  // winding: the axis test inverts a curled horn's inner curve and the underside of her jaw (the
  // final review, 2026-09-27: hands pushed 6 cm into her face in lie_front)
  const _sh1 = V();
  function skinGridOf(meshes) {
    let best = null, bn = 0;
    for (const m of meshes) { const n = m.geometry.attributes.position.count; if (n > bn) { bn = n; best = m; } }   // (her skin: the biggest body mesh)
    const g = new Set(), P = best.geometry.attributes.position, cell = 0.03;
    for (let i = 0; i < P.count; i++) { _sh1.fromBufferAttribute(P, i).applyMatrix4(best.bindMatrix); g.add(Math.floor(_sh1.x / cell) + "," + Math.floor(_sh1.y / cell) + "," + Math.floor(_sh1.z / cell)); }
    return { skin: best, g, cell };
  }
  function isCover(m, SG) {
    if (m === SG.skin) return false;
    const P = m.geometry.attributes.position, step = Math.max(1, Math.floor(P.count / 200));
    let n = 0, near = 0;
    for (let i = 0; i < P.count; i += step) {
      n++; _sh1.fromBufferAttribute(P, i).applyMatrix4(m.bindMatrix);
      const cx = Math.floor(_sh1.x / SG.cell), cy = Math.floor(_sh1.y / SG.cell), cz = Math.floor(_sh1.z / SG.cell);
      let hit = false;
      for (let a = -1; a <= 1 && !hit; a++) for (let b = -1; b <= 1 && !hit; b++) for (let c = -1; c <= 1 && !hit; c++) if (SG.g.has((cx + a) + "," + (cy + b) + "," + (cz + c))) hit = true;
      if (hit) near++;
    }
    return near > 0.6 * n;
  }
  function clearBuild() {
    CLR.dirty = false; CLR.ok = false;
    kindsOf();
    armSet.L = armSet.R = null;                  // (bones may have been grafted under an arm since)
    const meshes = [];
    root.traverse((o) => { if (o.isSkinnedMesh && o.visible && !/_ink$/.test(o.name) && o.geometry && o.geometry.attributes.skinIndex && o.geometry.attributes.normal) meshes.push(o); });
    if (!meshes.length || !B.upperarmL || !B.forearmL || !B.handL) return;
    const recs = [];            // per mesh: its joints, their pre-matrices (boneInverse·bindMatrix), world matrices
    const body = [], arms = { L: [], R: [] };
    const cellB = 0.022, cellA = 0.02;
    const seenB = new Set(), seenA = new Set();
    const vx = V(), nx = V(), hd = V(), sp = V();
    const SG = skinGridOf(meshes);
    for (const m of meshes) {
      const bones = m.skeleton.bones, inv = m.skeleton.boneInverses;
      const part = bones.map((b) => { if (!b) return "skip"; const mm = b.name.match(ARM_J); if (mm) return mm[2] || mm[4]; if (SKIP_J.test(b.name) || (b.userData && b.userData.rigChain) || chainKind[b.name]) return "skip"; return "body"; });
      // each joint's bind-time head and axis (to its first child that isn't a chain or a twist): for
      // a THIN SHELL garment (cloth modelled with an inner face) the normals are turned away from the
      // bone's axis — its inner face points IN, and read every arm near a hem or cuff as "inside" —
      // while a SOLID mesh (skin, horns) keeps its own winding: the axis test inverts a curled horn's
      // inner curve and the underside of her jaw (the final review, 2026-09-27: hands pushed 6 cm into
      // her face in lie_front)
      const heads = inv.map((iv) => new THREE.Vector3().setFromMatrixPosition(_cm.copy(iv).invert()));
      const tails = bones.map((b, k) => { const c = b && b.children.find((o) => o.isBone && !/Twist|^skirt_|^dread/.test(o.name)); const ci = c ? bones.indexOf(c) : -1; return ci >= 0 ? heads[ci] : heads[k]; });
      const rec = { m, bones, pre: inv.map((iv) => iv.clone().multiply(m.bindMatrix)), W: bones.map(() => new THREE.Matrix4()), used: new Set() };
      recs.push(rec);
      const g = m.geometry, P = g.attributes.position, N = g.attributes.normal, SI = g.attributes.skinIndex, SW = g.attributes.skinWeight;
      // A LOOSE garment (one riding physics chains: a skirt, a cape) is not her body — it yields to
      // her hands, it doesn't push them. Its bits weighted to a thigh (by a slit) pushed a hanging
      // hand 4 cm off its own thin, two-faced cloth (the arm jitter test, stand_up)
      // …what KIND of chain a mesh rides says what it is: a skirt/cape family = loose cloth; a hair
      // family = hair (a fraction of chain-riding vertices put her dreads just over a 20% line and her
      // hair stopped colliding at all — the final review)
      let clothV = 0, hairV = 0;
      for (let i = 0; i < P.count; i++) { let bw = -1, bj = 0; for (let k = 0; k < 4; k++) { const w = SW.getComponent(i, k); if (w > bw) { bw = w; bj = SI.getComponent(i, k); } } const ck = bones[bj] && chainKind[bones[bj].name]; if (ck === "hair") hairV++; else if (ck && ck !== "tail") clothV++; }
      const loose = clothV > 0.2 * P.count;
      // HAIR is soft: an arm may sink 3 cm into it (hands behind her head fought a 5.5 cm "push" out
      // of her dreads every frame, lie_back)
      const soft = !loose && hairV > 0.005 * P.count ? 0.03 : 0;
      const shell = isCover(m, SG);
      if (CLR.debug) CLR.debug.push([m.name, shell, loose, soft]);
      for (let i = 0; i < P.count; i++) {
        let bw = -1, bj = 0, armL = 0, armR = 0, skip = 0;
        for (let k = 0; k < 4; k++) {
          const w = SW.getComponent(i, k), j = SI.getComponent(i, k);
          if (w > bw) { bw = w; bj = j; }
          const p = part[j]; if (p === "L") armL += w; else if (p === "R") armR += w; else if (p === "skip") skip += w;
        }
        const dp = part[bj];
        vx.fromBufferAttribute(P, i).applyMatrix4(m.bindMatrix);             // bind-time world
        const isArm = (dp === "L" && armL >= 0.8) || (dp === "R" && armR >= 0.8);
        const isBody = !loose && dp === "body" && armL + armR < 0.2 && skip < 0.5;
        if (!isArm && !isBody) continue;
        const cell = isArm ? cellA : cellB;
        const key = (isArm ? dp : "B") + Math.floor(vx.x / cell) + "," + Math.floor(vx.y / cell) + "," + Math.floor(vx.z / cell);
        const seen = isArm ? seenA : seenB;
        if (seen.has(key)) continue;
        seen.add(key);
        nx.fromBufferAttribute(N, i).applyMatrix3(_cn.getNormalMatrix(m.bindMatrix)).normalize();
        segClosest(heads[bj], tails[bj], vx, sp);
        let flip = 1; if (shell && hd.subVectors(vx, sp).dot(nx) < 0) flip = -1;
        const js = [], ws = [];
        for (let k = 0; k < 4; k++) { const w = SW.getComponent(i, k); if (w > 1e-3) { js.push(SI.getComponent(i, k)); ws.push(w); rec.used.add(SI.getComponent(i, k)); } }
        const pt = { rec, js, ws, dom: bj, soft, domName: bones[bj].name, v: new THREE.Vector3().fromBufferAttribute(P, i), n: new THREE.Vector3().fromBufferAttribute(N, i).multiplyScalar(flip), rest: vx.clone(), restN: nx.clone().multiplyScalar(flip), P: V(), N: V(), seg: 0, t: 0, skipAt: null };
        if (isArm) arms[dp].push(pt); else body.push(pt);
      }
    }
    // arm points: which segment moves them (upper arm → the elbow, forearm → elbow AND wrist, hand →
    // the wrist) and how far along it, at bind time
    for (const k of ["L", "R"]) {
      // (the bind positions from a mesh that HAS the arm bones — a garment worn on its own subset
      // skeleton may come first)
      const r0 = recs.find((r) => r.bones.indexOf(B["upperarm" + k]) >= 0 && r.bones.indexOf(B["forearm" + k]) >= 0 && r.bones.indexOf(B["hand" + k]) >= 0);
      if (!r0) return;
      const iu = r0.bones.indexOf(B["upperarm" + k]), ifo = r0.bones.indexOf(B["forearm" + k]), ih = r0.bones.indexOf(B["hand" + k]);
      const inv0 = r0.m.skeleton.boneInverses;
      const S = new THREE.Vector3().setFromMatrixPosition(_cm.copy(inv0[iu]).invert()), E = new THREE.Vector3().setFromMatrixPosition(_cm.copy(inv0[ifo]).invert()), Wr = new THREE.Vector3().setFromMatrixPosition(_cm.copy(inv0[ih]).invert());
      for (const p of arms[k]) {
        const nm = p.domName;
        if (/^upperarm/.test(nm)) { p.seg = 0; p.t = segT(S, E, p.rest); }
        else if (/^forearm/.test(nm)) { p.seg = 1; p.t = segT(E, Wr, p.rest); }
        else { p.seg = 2; p.t = 1; }
      }
      // the upper arm's first third is the SHOULDER and the armpit fold: it creases into her side
      // when the arm lifts (that's the weights' job, not the pose's) — levering the elbow off it
      // splayed every forward reach (measured: the back of the armpit, t 0.24, "4.7 cm in")
      arms[k] = arms[k].filter((p) => p.seg !== 0 || p.t > 0.35);
    }
    // THE ARMPIT CALIBRATION: an arm point already touching her body at rest (within 1.5 cm: the
    // seam where her arm meets her side) ignores that same PATCH of body (4 cm round where it
    // touched, measured at rest) for good — but not the rest of that bone: an inner arm that rests
    // on the side of her chest still may not sink into the FRONT of it. (Skipping the whole bone
    // let raised arms sink 3-5 cm into her neck and chest — the meter, 2026-09-27.)
    for (const k of ["L", "R"]) for (const p of arms[k]) {
      if (p.seg !== 0) continue;                   // (only the upper arm meets her side at rest)
      let best = 1e9, bi = null;
      for (const q of body) { const d = q.rest.distanceToSquared(p.rest); if (d < best) { best = d; bi = q; } }
      if (!bi || best > 0.06 * 0.06) continue;
      const sd = hd.subVectors(p.rest, bi.rest).dot(bi.restN);
      if (sd < 0.015 || best < 0.015 * 0.015) p.skipAt = bi.rest;
    }
    // ARM ON ARM: each arm's coarse points (one per 3 cm) are a collider for the other arm's
    // forearm and hand (a clap meets palm to palm; a hand cupping an elbow rests on it)
    const coarse = {};
    for (const k of ["L", "R"]) {
      const seen = new Set(); coarse[k] = [];
      // (not its HAND: two hands meet freely — palm on palm, fingers laced; hand-on-hand pushes along
      // coarse finger normals shoved a clapping pair 5 cm down and back up every clap)
      for (const p of arms[k]) { if (p.seg === 2) continue; const key = Math.floor(p.rest.x / 0.03) + "," + Math.floor(p.rest.y / 0.03) + "," + Math.floor(p.rest.z / 0.03); if (!seen.has(key)) { seen.add(key); coarse[k].push(p); } }
    }
    CLR.coarse = coarse; CLR.bbs = { L: { min: V(), max: V() }, R: { min: V(), max: V() } };
    // the spatial hash (allocation-free per frame): heads per cell, a next-link per point
    CLR.recs = recs; CLR.body = body; CLR.arms = arms;
    CLR.TB = 8192; CLR.head = new Int32Array(CLR.TB); CLR.next = new Int32Array(body.length);
    CLR.ok = body.length > 0 && arms.L.length > 0 && arms.R.length > 0;
  }
  function segClosest(a, b, p, out) {
    _v3.subVectors(b, a); const L = _v3.lengthSq();
    const t = L > 1e-12 ? Math.max(0, Math.min(1, _v7.subVectors(p, a).dot(_v3) / L)) : 0;
    return out.copy(a).addScaledVector(_v3, t);
  }
  function segT(a, b, p) { _v3.subVectors(b, a); const L = _v3.lengthSq(); return L > 1e-12 ? Math.max(0, Math.min(1, _v7.subVectors(p, a).dot(_v3) / L)) : 0; }
  // skin one point with its mesh's current world matrices
  function skinPt(p) {
    const W = p.rec.W;
    p.P.set(0, 0, 0);
    for (let k = 0; k < p.js.length; k++) { _v1.copy(p.v).applyMatrix4(W[p.js[k]]); p.P.addScaledVector(_v1, p.ws[k]); }
    p.N.copy(p.n).transformDirection(W[p.dom]);
  }
  function clearMatrices(only) {
    for (const r of CLR.recs) for (const j of r.used) { if (only && !only.has(r.bones[j])) continue; r.W[j].multiplyMatrices(r.bones[j].matrixWorld, r.pre[j]); }
  }
  const CELL = 0.06;               // (27 cells a query, not 125: half the cost, the same hands — the final review)
  const hkey = (x, y, z) => (((x * 73856093) ^ (y * 19349663) ^ (z * 83492791)) >>> 0) % CLR.TB;
  function clearHash(s) {
    const { body, head, next } = CLR, c = CELL * s;
    head.fill(-1);
    for (let i = 0; i < body.length; i++) {
      const p = body[i]; skinPt(p);
      const h = hkey(Math.floor(p.P.x / c), Math.floor(p.P.y / c), Math.floor(p.P.z / c));
      next[i] = head[h]; head[h] = i;
    }
  }
  const NR = { i: -1, sd: 0 };
  // the other arm as a collider: its coarse points AS THE CLIP POSED IT (both arms measured before
  // either moves — each arm pushed off the other's corrected, filtered pose fed back into it and the
  // two hands under her chin buzzed 2-4 cm a frame), their bounding box
  function otherArms(s) {
    for (const o of SIDES2) {
      const bb = CLR.bbs[o]; bb.min.set(Infinity, Infinity, Infinity); bb.max.set(-Infinity, -Infinity, -Infinity);
      for (const q of CLR.coarse[o]) { skinPt(q); (q.R0 || (q.R0 = V())).copy(q.P); (q.N0 || (q.N0 = V())).copy(q.N); bb.min.min(q.P); bb.max.max(q.P); }
      const r = CLR.R * s; bb.min.addScalar(-r); bb.max.addScalar(r);
    }
  }
  function armNearest(p, o, s) {
    const bb = CLR.bbs[o];
    if (p.P.x < bb.min.x || p.P.y < bb.min.y || p.P.z < bb.min.z || p.P.x > bb.max.x || p.P.y > bb.max.y || p.P.z > bb.max.z) return false;
    let best = (0.035 * s) ** 2, bq = null;
    for (const q of CLR.coarse[o]) { const d2 = q.R0.distanceToSquared(p.P); if (d2 < best) { best = d2; bq = q; } }
    if (!bq) return false;
    const sd = _v2.subVectors(p.P, bq.R0).dot(bq.N0);
    if (sd >= NR.sd) return false;                     // the body is the nearer surface
    NR.sd = sd; NR.nx = bq.N0.x; NR.ny = bq.N0.y; NR.nz = bq.N0.z; NR.i = -1;
    return true;
  }
  // the signed distance to her surface near p: the THREE nearest surface points, blended by nearness
  // (one nearest point snaps between neighbours in a crease and the push jumps with it)
  const _nb = [0, 0, 0], _nd = [0, 0, 0];
  function clearNearest(p, s) {
    const { body, head, next } = CLR, c = CELL * s, R = CLR.R * s;
    const cx = Math.floor(p.P.x / c), cy = Math.floor(p.P.y / c), cz = Math.floor(p.P.z / c), rr = Math.ceil(R / c);
    _nb[0] = _nb[1] = _nb[2] = -1; _nd[0] = _nd[1] = _nd[2] = R * R;
    for (let a = -rr; a <= rr; a++) for (let b = -rr; b <= rr; b++) for (let d = -rr; d <= rr; d++) {
      for (let i = head[hkey(cx + a, cy + b, cz + d)]; i >= 0; i = next[i]) {
        const q = body[i];
        const d2 = q.P.distanceToSquared(p.P);
        if (d2 >= _nd[2] || i === _nb[0] || i === _nb[1]) continue;
        if (d2 < _nd[0]) { _nd[2] = _nd[1]; _nb[2] = _nb[1]; _nd[1] = _nd[0]; _nb[1] = _nb[0]; _nd[0] = d2; _nb[0] = i; }
        else if (d2 < _nd[1]) { _nd[2] = _nd[1]; _nb[2] = _nb[1]; _nd[1] = d2; _nb[1] = i; }
        else { _nd[2] = d2; _nb[2] = i; }
      }
    }
    if (_nb[0] < 0) return false;
    let ws = 0, sd = 0, nx = 0, ny = 0, nz = 0, soft = 0;
    for (let k = 0; k < 3; k++) {
      if (_nb[k] < 0) break;
      const q = body[_nb[k]], w = 1 / (Math.sqrt(_nd[k]) + 0.004 * s);
      sd += w * _v2.subVectors(p.P, q.P).dot(q.N); nx += w * q.N.x; ny += w * q.N.y; nz += w * q.N.z; soft += w * q.soft; ws += w;
    }
    // (hair's softness blended like the rest: taken from the single nearest point it switched 3 cm on
    // and off as the nearest flipped between hair and skin — a 14 mm hitch in a held think)
    NR.i = _nb[0]; NR.sd = (sd + soft * s) / ws;
    // the seam where this arm point met her body at rest may stay 4 cm "in" — fading to nothing 3-6 cm
    // away from it, never a hard edge (a hard skip let a point cross its edge already 4 cm deep and
    // the arm popped out in one frame — the arm jitter test, stand_up)
    if (p.skipAt) { const dq = Math.sqrt(body[_nb[0]].rest.distanceToSquared(p.skipAt)); NR.sd += 0.04 * s * (1 - ramp01(dq, 0.03, 0.06)); }
    const l = Math.hypot(nx, ny, nz) || 1; NR.nx = nx / l; NR.ny = ny / l; NR.nz = nz / l;
    return true;
  }
  const clearSnap = { L: 0, R: 0 }, clearPin = { L: false, R: false };
  const _cE = V(), _cW = V(), _cS = V(), _cEl = V(), _cWr = V(), _cP = V(), _cU = V(), _cQ = Qn(), _cQ2 = Qn();
  const armSet = { L: null, R: null };
  // THE FILTER: every frame the arm is solved afresh from the clip's own pose (a correction that
  // PERSISTED got trapped on the wrong side of her: a forearm swept through her belly on the way
  // in, was pushed out behind her, and could never come back through — 30 cm off its place, for
  // good; reach.mjs). The correction — how far the elbow and wrist moved, in her CHEST's frame —
  // is then smoothed evenly both ways (fast in, slow out sawtoothed: let go, sank, snapped out).
  const CF = { L: { E: V(), W: V(), on: false }, R: { E: V(), W: V(), on: false } };
  const _cE0 = V(), _cW0 = V(), _cS0 = V(), _cqH = Qn(), _cch = Qn(), _cqa = Qn(), _cqb = Qn(), _cqc = Qn();
  function placeArm(k, R, E, W, s) {       // the wrist to W, the elbow bending toward E; the hand keeps its world turn
    _cP.addVectors(_cS0, W).multiplyScalar(0.5);
    _cU.subVectors(E, _cP);
    if (_cU.lengthSq() < 1e-10) return false;
    solveIK(k, W, _cU.normalize(), 1);
    R.c.parent.getWorldQuaternion(_cQ2).invert();
    R.c.quaternion.copy(_cQ2.multiply(_cqH));
    R.c.updateMatrixWorld(true);
    return true;
  }
  function clearArms(wgt, dt) {
    if (CLR.dirty) { clearBuild(); CF.L.on = CF.R.on = false; }
    if (!CLR.ok || wgt <= 0) return;
    const s = scaleOf();
    root.updateMatrixWorld(true);
    clearMatrices(null);
    clearHash(s);
    otherArms(s);
    const m = CLR.M * s, maxStep = 0.08 * s;
    worldQ(B.chest || arm, _cch);
    for (const k of SIDES2) {
      const pts = CLR.arms[k], R = restOf(k); if (!R) continue;
      if (!armSet[k]) { armSet[k] = new Set(); R.a.traverse((o) => { if (o.isBone) armSet[k].add(o); }); }
      const o = k === "L" ? "R" : "L";
      const snap = clearSnap[k] * wgt;
      const F = CF[k];
      CLR.last[k].pen = 0; CLR.last[k].arm = CLR.last[k].body = "";     // (what it pushed hardest this frame: rig.clearance.last)
      touch(R.a); touch(R.b); touch(R.c);
      _cqa.copy(R.a.quaternion); _cqb.copy(R.b.quaternion); _cqc.copy(R.c.quaternion);
      R.a.getWorldPosition(_cS0); R.b.getWorldPosition(_cE0); R.c.getWorldPosition(_cW0); R.c.getWorldQuaternion(_cqH);
      let moved = false;
      // push out what sinks in (and spring a snapping hand to its surface) — not while the clip is
      // letting go: her arm is going home to the game's own pose (a fresh push at a fading weight
      // jumped 4.5 cm in one frame — stand_up's last 0.2 s, the arm jitter test)
      for (let it = 0; it < (player.stopping ? 0 : 4); it++) {
        if (it) { R.a.updateMatrixWorld(true); clearMatrices(armSet[k]); }
        R.a.getWorldPosition(_cS); R.b.getWorldPosition(_cEl); R.c.getWorldPosition(_cWr);
        let eW = 0, wW = 0, any = false, snapSd = Infinity;
        _cE.set(0, 0, 0); _cW.set(0, 0, 0);
        let sx = 0, sy = 0, sz = 0;
        for (const p of pts) {
          skinPt(p);
          let hit = clearNearest(p, s);
          if (!hit) NR.sd = Infinity;
          if (p.seg > 0 && armNearest(p, o, s)) hit = true;
          if (!hit) continue;
          // a SNAPPING hand is one spring to the surface (below): its closest point, pushed or pulled
          if (p.seg === 2 && snap > 0) { if (NR.sd < snapSd) { snapSd = NR.sd; sx = NR.nx; sy = NR.ny; sz = NR.nz; } continue; }
          const pen = m - NR.sd;
          if (pen <= 0) continue;
          any = true;
          if (it === 0 && pen > CLR.last[k].pen) { const L = CLR.last[k]; L.pen = pen; L.arm = p.domName; L.body = NR.i >= 0 && NR.i < CLR.body.length ? CLR.body[NR.i].domName : "arm" + o; }
          _cP.set(NR.nx, NR.ny, NR.nz).multiplyScalar(Math.min(pen, 0.15 * s));
          const mag = _cP.lengthSq();
          if (p.seg === 0) { _cU.copy(_cP).multiplyScalar(1 / Math.max(p.t, 0.4)); _cE.addScaledVector(_cU, mag); eW += mag; }
          else if (p.seg === 1) {
            const t = p.t, den = (1 - t) * (1 - t) + t * t;
            _cU.copy(_cP).multiplyScalar((1 - t) / den); _cE.addScaledVector(_cU, mag); eW += mag * (1 - t);
            _cU.copy(_cP).multiplyScalar(t / den); _cW.addScaledVector(_cU, mag); wW += mag * t;
          } else { _cW.addScaledVector(_cP, mag); wW += mag; }
        }
        // SNAP, continuous (never a switch between "pushed" and "pulled" — that buzzed): the hand's
        // closest point is sprung to the surface — fully when it's inside, fading out with the gap
        if (snap > 0 && snapSd < CLR.SNAP * s) {
          const gap = snapSd - m, fade = gap <= 0 ? 1 : snap * (1 - ramp01(gap / (CLR.SNAP * s - m), 0.6, 1));
          _cP.set(sx, sy, sz).multiplyScalar(-gap * fade);
          if (_cP.lengthSq() > 1e-8 * s * s) { const mag = _cP.lengthSq(); _cW.addScaledVector(_cP, mag); wW += mag; any = true; }
        }
        if (!any) break;
        if (eW > 0) _cE.divideScalar(eW);
        if (wW > 0) _cW.divideScalar(wW);
        // the elbow's push ALONG the shoulder→wrist line can't be done by swinging it: the wrist takes it
        _cU.subVectors(_cWr, _cS).normalize();
        const par = _cE.dot(_cU);
        _cE.addScaledVector(_cU, -par);
        if (!clearPin[k]) _cW.addScaledVector(_cU, par * 0.5); else _cW.set(0, 0, 0);
        if (_cE.length() > maxStep) _cE.setLength(maxStep);
        if (_cW.length() > maxStep) _cW.setLength(maxStep);
        _cWr.add(_cW); _cEl.add(_cE);
        if (!placeArm(k, R, _cEl, _cWr, s)) break;
        moved = true;
      }
      // this frame's correction, in her chest's frame (metres of her), times the clip's weight
      _cQ.copy(_cch).invert();
      if (moved) { R.b.getWorldPosition(_cEl); R.c.getWorldPosition(_cWr); _cEl.sub(_cE0).applyQuaternion(_cQ).divideScalar(s); _cWr.sub(_cW0).applyQuaternion(_cQ).divideScalar(s); }
      else { _cEl.set(0, 0, 0); _cWr.set(0, 0, 0); }
      if (!F.on) { F.E.copy(_cEl); F.W.copy(_cWr); F.on = true; }
      else { const a = 1 - Math.exp(-(dt || 0.016) * 18); F.E.lerp(_cEl, a); F.W.lerp(_cWr, a); }
      // …applied to the clip's own arm
      R.a.quaternion.copy(_cqa); R.b.quaternion.copy(_cqb); R.c.quaternion.copy(_cqc);
      R.a.updateMatrixWorld(true);
      if (F.E.lengthSq() + F.W.lengthSq() < 1e-12) continue;
      _cEl.copy(F.E).multiplyScalar(s).applyQuaternion(_cch).add(_cE0);
      _cWr.copy(F.W).multiplyScalar(s).applyQuaternion(_cch).add(_cW0);
      placeArm(k, R, _cEl, _cWr, s);
    }
  }
  const ramp01 = (x, a, b) => Math.min(1, Math.max(0, (x - a) / (b - a)));

  function runPosts() {
    for (const d of posts) {
      touch(d.b);
      if (d.kind === "twist") d.b.quaternion.setFromAxisAngle(d.axis, d.k * twistAngle(d.of.quaternion, d.axis));
      else if (d.kind === "half") d.b.quaternion.slerpQuaternions(QI, d.of.quaternion, d.k !== undefined ? d.k : 0.5);
    }
  }
  // (hoisted out of apply: nothing allocates per frame — THE PERF LAW)
  const ARM_SP = /^(upperarm|forearm|hand|clavicle)(L|R)$|^(index|middle|ring|pinky|thumb)(L|R)\d$/;
  const armSideOf = (sp) => { const m = sp && ARM_SP.exec(sp); return m ? m[2] || m[4] : null; };
  const onArm = (g, k) => { if (!g) return false; const sd = armSideOf(g.on ? g.on[0] : g.to && g.to[0]); return !!sd && sd !== k; };
  const snapOf = (g) => (!g ? 0 : g.snap === true ? 1 : g.snap !== undefined ? +g.snap || 0 : g.on ? 1 : 0);   // an `on` goal snaps by default
  const SIDES2 = ["L", "R"];

  // ── public ──
  const rig = {
    ok: true, ieh, B, player, chains, cols, extraCols,
    play, stop, evalClip, solveIK, wear, wearing,
    refit() { CLR.dirty = true; PSD.dirty = true; },        // her meshes changed (a garment on/off): re-measure
    get clearance() { return CLR; },
    get clearCarry() { return CF; },
    ikLast,
    get clip() { return player.clip; }, get clipT() { return player.t; }, get weight() { return player.w; },
    snap() { for (const c of chains) c.init = false; CF.L.on = CF.R.on = false; },     // a camera cut or a teleport: springs (and the arm filter) start fresh

    begin() {
      for (const b of dirty) { const q = touched.get(b); if (q) b.quaternion.copy(q); }
      for (const b of dirtyPos) { const p = touchedPos.get(b); if (p) b.position.copy(p); }
      dirty.clear(); dirtyPos.clear();
      fresh = true; clavDone = false;
    },

    // THE CLIP LAYER: over whatever the game and the mood wrote this frame
    apply(t, dt, ctl) {
      if (!fresh) return null;
      lastCtl = ctl || null;
      ikLast.L.w = ikLast.R.w = ikLast.footL.w = ikLast.footR.w = 0;
      clearSnap.L = clearSnap.R = 0; clearPin.L = clearPin.R = false;
      // ctl.colliders: the scene's props, in WORLD units — { box: { min, max } } (a chair
      // seat, a step) or { a: [x, y, z], b: [x, y, z], r } (a railing, a log)
      if (ctl && ctl.colliders !== lastCols) {
        lastCols = ctl.colliders;
        extraCols.length = 0;
        for (const c of lastCols || []) {
          if (c.box) extraCols.push({ box: { min: new THREE.Vector3(...c.box.min), max: new THREE.Vector3(...c.box.max) } });
          else if (c.a && c.b) extraCols.push({ a: new THREE.Vector3(...c.a), b: new THREE.Vector3(...c.b), r: c.r || 0.05 });
        }
      }
      if (ctl && ctl.clip !== undefined && ctl.clip !== player.key) {
        player.key = ctl.clip;
        if (ctl.clip) play(ctl.clip, ctl.clipOpts || {}); else stop();
      }
      const P = player;
      if (!P.clip) return null;
      const clip = P.clip;
      P.t += dt * P.speed;
      const dur = clip.dur || 1;
      if (P.t >= dur) {
        if (P.loop) P.t %= dur;
        else { P.t = dur; if (!clip.hold) P.stopping = true; P.done = true; }
      }
      if (P.stopping) P.w = Math.max(0, P.w - dt / Math.max(P.fadeOut, 1e-3));
      else P.w = Math.min(1, P.w + dt / Math.max(P.fadeIn, 1e-3));
      if (P.w <= 0 && P.stopping) { P.clip = null; P.stopping = false; lastQ.clear(); xf.on = false; return null; }
      const w = P.w * P.w * (3 - 2 * P.w);
      P.life += dt * P.speed;
      const out = evalClip(clip, P.t, P.life);
      for (const n of clipBones(clip)) { const b = B[n]; touch(b); b.quaternion.slerp(cur[n], w); }
      if (B.hips) { touchPos(B.hips); B.hips.position.addScaledVector(out.hips, w * FIT.L); }
      if (out.root && B.root) {
        touch(B.root); touchPos(B.root);
        B.root.position.addScaledVector(out.rootP, w * FIT.H);
        _q1.setFromEuler(_e.set(out.rootR.x * D2R, out.rootR.y * D2R, out.rootR.z * D2R, "XYZ"));
        _q2.copy(B.root.quaternion).multiply(_q1);
        B.root.quaternion.slerp(_q2, w);
      }
      root.updateMatrixWorld(true);
      // GROUND before IK (a planted limb must plant on the grounded body): lift or drop the
      // hips so her lowest point sits on the floor — in the hips' parent space (she may lie down)
      // ground: "lowest" lands her lowest point on the floor AFTER the limbs are placed (a
      // cross-legged sit: straight FK legs would lift her back up to standing); "fk" does it
      // before the IK (a planted foot must plant on the grounded body)
      if (out.ground === "fk" && B.hips) groundHips(w);
      if (clip.seat && B.hips) seatFit(w);
      driveClavicles();                         // the shoulders BEFORE the hands are placed
      if (out.ikA || out.ikB) {
        root.updateMatrixWorld(true);
        const e = out.e;
        // a hand whose goal rides the OTHER arm (cupping its elbow) is placed after that arm is
        // (in the plain order her left hand chased where her right elbow was BEFORE it moved)
        const order = onArm(out.ikA && out.ikA.L, "L") || onArm(out.ikB && out.ikB.L, "L") ? LIMBS_RL : LIMBS_LR;
        for (const k of order) {
          const ga = out.ikA && out.ikA[k], gb = out.ikB && out.ikB[k];
          if (!ga && !gb) continue;
          let wk;
          if (ga && gb && ga !== gb) {
            if (!goalWorld(ga, _tg, k) || !goalWorld(gb, _tg2, k)) continue;
            _tg.lerp(_tg2, e);
            poleOf(ga, _pl); poleOf(gb, _pl2); _pl.lerp(_pl2, e);
            if (_pl.lengthSq() < 1e-8) poleOf(gb, _pl);
            wk = (ga.w !== undefined ? ga.w : 1) * (1 - e) + (gb.w !== undefined ? gb.w : 1) * e;
          } else {
            const g = ga || gb;
            if (!goalWorld(g, _tg, k)) continue;
            poleOf(g, _pl);
            wk = (g.w !== undefined ? g.w : 1) * (ga === gb ? 1 : ga ? 1 - e : e);
          }
          // THE PALM, not the wrist: a goal with `palm: d` puts the middle of her palm there (the
          // wrist sits d back along where the fingers point) — a hand "on her knee" has its palm on
          // it; placing the wrist there left the hand holding air a hand-length past the elbow it
          // was meant to cup (critic, pose-refinement 2026-09-27)
          if (k === "L" || k === "R") {
            const pa = ga && ga.palm ? ga.palm : 0, pb = gb && gb.palm ? gb.palm : 0;
            const pd = ga && gb && ga !== gb ? pa * (1 - e) + pb * e : ga === gb ? pa : ga ? pa : pb;
            // (its own vector: scaleOf() writes _v7 — passing _v7 here made every palm offset (1,1,1))
            if (pd > 0) { const sc = scaleOf(); if (fingersWorld(k, out, _fw2)) _tg.addScaledVector(_fw2, -pd * sc); }
          }
          const hg = ga && gb && ga !== gb ? (ga.hinge || 0) * (1 - e) + (gb.hinge || 0) * e : ((ga || gb).hinge || 0) * (ga === gb ? 1 : ga ? 1 - e : e);
          solveIK(k, _tg, _pl.normalize(), w * wk, hg);
          ikLast[k].copy(_tg); ikLast[k].w = w * wk;            // (where it was sent: rig.ikLast, for the lab — in her HIPS' frame,
          if (B.hips) B.hips.worldToLocal(ikLast[k]);              //  so a clip grounded after its IK still compares)
          // what the arm clearance needs to know about this hand: pull it onto a surface (snap), or
          // keep its wrist where it is (planted on the floor)
          if (k === "L" || k === "R") {
            const sa = snapOf(ga), sb = snapOf(gb);
            clearSnap[k] = ga && gb && ga !== gb ? sa * (1 - e) + sb * e : ga === gb ? sa : ga ? sa * (1 - e) : sb * e;
            const gp = (e < 0.5 ? ga : gb) || ga || gb;
            clearPin[k] = !!gp && !!gp.to && gp.to[0] === "floor" && wk > 0.5;
          }
          // A PLANTED FOOT LIES FLAT: a foot goal on the floor keeps the sole level (only its heading
          // stays), or the shin's swing rolls it heel↔toe and the sole slides and sinks (the round-2
          // critic measured 30-110 cm of "slide" that was rolling). { flat: false } for a heel-up foot.
          const gf = gb || ga;
          if ((k === "footL" || k === "footR") && gf.to && gf.to[0] === "floor" && gf.flat !== false) flatFoot(B[k], w * wk);
        }
      }
      // hands with an AIM are turned last, in their arm's final frame (blended the same way)
      if (out.aimA || out.aimB) {
        root.updateMatrixWorld(true);
        for (const k of ["L", "R"]) {
          const a = out.aimA && out.aimA[k], b = out.aimB && out.aimB[k];
          if (!a && !b) continue;
          if (a && b && a !== b) aimHand(k, a, w, b, out.e);
          else aimHand(k, a || b, w * (a === b ? 1 : a ? 1 - out.e : out.e));
        }
      }
      if (out.ground === "lowest" && B.hips) groundHips(w);
      if (xf.on) {
        xf.k = Math.min(1, xf.k + dt / Math.max(P.fadeIn, 1e-3));
        const k = xf.k * xf.k * (3 - 2 * xf.k);
        for (const [b, q] of xf.q) { touch(b); _xq.copy(b.quaternion); b.quaternion.slerpQuaternions(q, _xq, k); }
        if (B.hips) { touchPos(B.hips); _xv.copy(B.hips.position); B.hips.position.lerpVectors(xf.hips, _xv, k); }
        root.updateMatrixWorld(true);
        if (xf.k >= 1) xf.on = false;
      }
      lastQ.clear();
      for (const b of dirty) lastQ.set(b, (xf.last.get(b) || xf.last.set(b, Qn()).get(b)).copy(b.quaternion));
      if (B.hips) lastHips.copy(B.hips.position);
      applyOut.w = w; applyOut.hands = out.hands; applyOut.fingers = out.fingers; applyOut.face = out.face; applyOut.props = out.props; applyOut.t = P.t; applyOut.done = P.done;
      return applyOut;
    },

    // DRIVERS + CHAINS: after every layer has written the bones
    end(dt) {
      if (!fresh) return;                        // no begin(), no post layers (they would compound)
      fresh = false;
      _dtRaw = Math.min(Math.max(dt || 0.016, 1e-4), 0.25);
      dt = Math.min(_dtRaw, 0.05);
      root.updateMatrixWorld(true);
      driveClavicles();
      runPosts();
      // ARM CLEARANCE: after every layer (the twists already set, so the arm's skin is measured as
      // drawn), then the twists again for the arm it re-solved
      // (a FORMED body — ieForm.js — keeps it on standing still too: its arms still hang where the base body's
      //  did, and wider hips or a giant arm would meet them; opts.formClear: false turns that off)
      const clr = opts.clearance !== false && !(lastCtl && lastCtl.clear === false) &&
        ((player.clip && player.w > 0 && player.clip.clear !== false) || (lastCtl && lastCtl.clear === true) || (ieh.form && opts.formClear !== false));
      if (clr) {
        try { clearArms(player.clip ? player.w * player.w * (3 - 2 * player.w) : 1, dt); runPosts(); }
        catch (e) { CLR.ok = false; CLR.dirty = false; console.warn("[ieRig] arm clearance off:", e); }
      } else CF.L.on = CF.R.on = false;          // (a clip stopped dead: the next one starts its filter fresh)
      root.updateMatrixWorld(true);
      driveShoulders();
      if (!chains.length) return;
      root.updateMatrixWorld(true);
      const s = scaleOf(), fl = floorY();
      _sNow = s;
      _tele = (ieh.model_top || 1.76) * s;                  // a body height in one frame = a teleport
      // an IN-PLACE clip that stands for travel (the run, the worm) blows its air through the cloth
      _wind.set(0, 0, 0);
      if (player.clip && player.travel) _wind.set(0, 0, 1).transformDirection(arm.matrixWorld).multiplyScalar(player.travel * s * player.w);
      for (const c of colList) {
        if (c.pa) { c.A.copy(c.pa); c.a.localToWorld(c.A); c.Bp.copy(c.pb); c.b.localToWorld(c.Bp); }
        else { c.a.getWorldPosition(c.A); c.b.getWorldPosition(c.Bp); }
      }
      worldQ(B.hips, _hq);
      B.hips.getWorldPosition(_hc);
      if (!Number.isFinite(_hq.w + _hq.x + _hq.y + _hq.z + _hc.x + _hc.y + _hc.z + s + fl)) { for (const ch of chains) ch.init = false; return; }
      _v1.set(0, -1, 0).applyQuaternion(_hq);
      _tilt.setFromUnitVectors(_v1, DOWN);        // the turn that brings her "down" back to the world's
      _fwd.set(0, 0, 1).applyQuaternion(_hq);
      B.hips.getWorldPosition(_hc);
      for (const fam of famList) {
        for (const ch of fam.list) chainPoints(ch, dt, s, fl);
        if (fam.pairs.length) rings(fam, s, fl);
        for (const ch of fam.list) chainWrite(ch);
      }
    },
  };

  // ── THE CHAIN SOLVER (kinematic: no jitter, no drift) ──
  const _hq = Qn(), _tilt = Qn(), _fwd = V(), _hc = V();
  let _sNow = 1, _tele = 1.76, _dtRaw = 0.016;
  const _wind = V();
  const _fq = Qn(), _fqL = Qn(), _fqR = Qn(), _hinv = Qn(), _tq = Qn(), _tq2 = Qn();
  const _a = V(), _b = V(), _n = V(), _dir = V(), _cur = V(), _tmp = V(), _acc = V();
  // a thigh's turn relative to the hips, as a world rotation: world(thigh)·world(hips)⁻¹
  const thighTurn = (th, out) => { worldQ(th, out); _hinv.copy(_hq).invert(); return out.multiply(_hinv); };
  const fwdSwing = (q) => { _tmp.set(0, -1, 0).applyQuaternion(q); return _tmp.dot(_fwd); };

  function chainPoints(ch, dt, s, fl) {
    const { bones, n, lens, P, A, D, W, F } = ch;
    for (const b of bones) touch(b);
    for (let i = 0; i < n; i++) ch.cl[i] = lens[i] * s;
    for (let i = 0; i < n; i++) bones[i].getWorldPosition(A[i]);
    _tmp.copy(ch.tipLocal); bones[n - 1].localToWorld(_tmp); A[n].copy(_tmp);
    // how fast the chain's root moves through the world (the cloth trails behind a run)
    if (ch.init && A[0].distanceToSquared(ch.last) > _tele * _tele) ch.init = false;      // a teleport: start fresh
    if (ch.init) {
      _acc.subVectors(A[0], ch.last).divideScalar(Math.max(_dtRaw, 1e-3)).add(_wind);
      ch.vel.lerp(_acc, 1 - Math.exp(-dt * 12));
    } else ch.vel.set(0, 0, 0);
    ch.last.copy(A[0]);
    // FOLLOW: the skirt goes with the thighs — the slit-side chains most, the back ones least,
    // the front panel with whichever thigh swings FORWARD more (it lies on that thigh)
    let fq = null;
    const follow = F.follow || 0;
    if (ch.kind === "skirt" && follow > 0) {
      if (ch.side === 0) {
        thighTurn(thighs.L, _fqL); thighTurn(thighs.R, _fqR);
        // the front goes with whichever thigh swings forward more; the back with the one behind —
        // BLENDED across a band: a hard pick flipped between two equal thighs every few frames in
        // a held sit (a 5 cm flicker of the front panel, 2026-09-27)
        const dL = fwdSwing(_fqL) - fwdSwing(_fqR);
        const u = Math.min(1, Math.max(0, 0.5 + (ch.front ? dL : -dL) * 4));        // 1 = the left thigh
        _fq.slerpQuaternions(_fqR, _fqL, u);
      } else thighTurn(ch.side > 0 ? thighs.L : thighs.R, _fq);
      const k = follow * ch.followK;
      _tq.copy(QI).slerp(_fq, Math.min(1, k));
      // …and never more than followMax: past it the cloth slides off the leg, the capsule pushes it
      const ang = 2 * Math.acos(Math.min(1, Math.abs(_tq.w))), mx = F.followMax || 0.7;
      if (ang > mx) { _tq2.copy(_tq); _tq.copy(QI).slerp(_tq2, mx / ang); }
      fq = _tq;
    }
    if (!ch.init) for (let i = 0; i < n; i++) { D[i].subVectors(A[i + 1], A[i]).normalize(); W[i].set(0, 0, 0); }
    P[0].copy(A[0]);
    for (let i = 0; i < n; i++) {
      _dir.subVectors(A[i + 1], A[i]).normalize();
      if (fq) _dir.applyQuaternion(fq);
      // GRAVITY: the lower cloth turns back toward hanging when her hips tip (lying, the worm)
      const g = F.tilt ? (F.tilt[i] !== undefined ? F.tilt[i] : F.tilt[F.tilt.length - 1]) : 0;
      if (g > 0) { _tq2.copy(QI).slerp(_tilt, g); _dir.applyQuaternion(_tq2); }
      // DRAG: moving through the air bends the cloth back
      const dr = F.drag ? F.drag * (i + 1) / n : 0;
      if (dr > 0) { _dir.addScaledVector(ch.vel, -dr / Math.max(s, 1e-3)).normalize(); }
      // FOLLOW-THROUGH: SmoothDamp on the direction (critically damped, stable at any dt)
      const lag = F.spring ? (F.spring[i] !== undefined ? F.spring[i] : F.spring[F.spring.length - 1]) : 0;
      if (lag > 0 && ch.init) {
        const om = 2 / (0.25 * Math.min(1, lag)), x = om * dt, ex = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
        _cur.subVectors(D[i], _dir);
        _tmp.copy(W[i]).addScaledVector(_cur, om).multiplyScalar(dt);
        W[i].addScaledVector(_tmp, -om).multiplyScalar(ex);
        D[i].copy(_dir).addScaledVector(_cur.add(_tmp), ex).normalize();
        // never flail: at most 0.7 rad behind where it's going — CLAMPED, not yanked halfway back
        // (cloth resting on her thighs sits past that from where it would hang; the halfway yank
        // and the thighs' slow push-back made a 5-frame flicker in a held sit, 2026-09-27)
        const ag = D[i].angleTo(_dir);
        if (ag > 0.7) { _cur.crossVectors(_dir, D[i]); if (_cur.lengthSq() > 1e-12) D[i].copy(_dir).applyAxisAngle(_cur.normalize(), 0.7); else D[i].copy(_dir); }
      } else { D[i].copy(_dir); W[i].set(0, 0, 0); }
      P[i + 1].copy(P[i]).addScaledVector(D[i], lens[i] * s);
      contact(ch, i, s, fl);
    }
    ch.init = true;
  }
  // push segment i out of the capsules, the scene's boxes and the floor, keeping its length
  // THE CLOTH'S OFFSET: a garment's cloth hangs `inset` inside its chain's line (the ring is laid
  // round her body, not the garment). That offset turns with the segment: hanging, it points in
  // toward her; lying out across a seat or the floor, it points DOWN into it. Seats and floors
  // test the cloth, not the chain line (2026-09-27: her skirt hung 4-11 cm inside a chair box).
  const _co = V(), _cr = V(), _cd = V(), _coq = Qn();
  function clothOff(ch, i, s, out) {
    out.set(0, 0, 0);
    const k = ch.inset ? ch.inset[i] || 0 : 0;
    if (k <= 0) return out;
    _cr.copy(ch.rdir[i]).applyQuaternion(_hq);                     // the segment at rest, in the world (her hips)
    _cd.subVectors(ch.P[i + 1], ch.P[i]);
    if (_cd.lengthSq() < 1e-12) return out;
    _coq.setFromUnitVectors(_cr, _cd.normalize());
    return out.copy(ch.inw).applyQuaternion(_hq).applyQuaternion(_coq).multiplyScalar(k * s);
  }
  function contact(ch, i, s, fl) {
    const { P, F } = ch;
    let L = ch.cl[i];
    const margin = (F.margin || 0.015) * s;
    const list = F.collide || [];
    const front = ch.kind === "skirt" && ch.front && ch.side === 0;
    for (let pass = 0; pass < 2; pass++) {
      clothOff(ch, i, s, _co);
      for (let ci = 0; ci < list.length; ci++) {
        const c = cols[list[ci]];
        if (!c) continue;
        const r = ch.clear[i][list[ci]] * s;
        const hit = segCapsule(P[i], P[i + 1], c.A, c.Bp, _n);
        if (hit.d < r) {
          // the front panel is only ever pushed FORWARD off a thigh (never onto the other leg);
          // every other skirt chain is never pushed IN toward her midline (a leg sweeping under the
          // front-side chains flipped them across onto the other thigh, 2026-09-27)
          if (front) { _n.addScaledVector(_fwd, 1.5).normalize(); }
          else if (ch.kind === "skirt") {
            _v3.copy(ch.inw).applyQuaternion(_hq).negate();
            const dn = _n.dot(_v3);
            // MIRRORED, not projected: projecting turned a mostly-inward push into a full-strength
            // sideways shove that never resolved (a held chair sit cycled every 6 frames)
            if (dn < 0) { _n.addScaledVector(_v3, -2 * dn); _n.normalize(); }
          }
          P[i + 1].addScaledVector(_n, (r - hit.d) / Math.max(hit.s, 0.35));
          relength(P, i, L);
        }
      }
      for (const c of extraCols) {
        if (c.box) L = ch.cl[i] = boxContact(ch, i, c.box, L, margin, _co, s);
        else {
          const hit = segCapsule(P[i], P[i + 1], c.a, c.b, _n), r = c.r + margin;
          if (hit.d < r) { P[i + 1].addScaledVector(_n, (r - hit.d) / Math.max(hit.s, 0.35)); relength(P, i, L); }
        }
      }
      const fm = fl + margin * 0.6 - Math.min(0, _co.y);
      if (P[i + 1].y < fm) {
        // POOL: the cloth lies on the floor, out the way this chain faces on her (a ring chain:
        // its azimuth; hair/tail: away from her middle), leaning the way it was already going
        // when that agrees. ("Away from her middle" alone flips when the point sits under her —
        // a held floor sit flickered 32 cm every frame, 2026-09-27.)
        // Lying down, "out" turns toward the sky and her own DOWN (to her feet) takes over: the two
        // are summed, so the blend is smooth from standing to lying. Every weight here is continuous
        // (a threshold between two directions is a flicker waiting to happen).
        const dy = fm - P[i].y;
        if (ch.inw.lengthSq() > 0.5) { _dir.copy(ch.inw).applyQuaternion(_hq).negate(); _dir.y = 0; } else _dir.set(0, 0, 0);
        _cur.set(0, -1, 0).applyQuaternion(_hq); _cur.y = 0;
        _dir.add(_cur);
        if (_dir.lengthSq() < 1e-4) { _dir.subVectors(P[i + 1], _hc); _dir.y = 0; }
        if (_dir.lengthSq() < 1e-8) { _dir.subVectors(P[i + 1], P[i]); _dir.y = 0; }
        if (_dir.lengthSq() < 1e-8) _dir.set(0, 0, 1);
        _dir.normalize();
        _cur.subVectors(P[i + 1], P[i]); _cur.y = 0;
        if (_cur.lengthSq() > 1e-8) {
          const agree = Math.min(1, Math.max(0, (_cur.normalize().dot(_dir) - 0.3) / 0.5));
          if (agree > 0) _dir.lerp(_cur, 0.5 * agree).normalize();
        }
        // (a zig-zag "crumple" was tried here, 2026-09-27: neighbouring chains folded out of
        // step and the ink drew a black scribble — flat pooling reads better in toon)
        const h = Math.sqrt(Math.max(0, L * L - dy * dy));
        P[i + 1].copy(P[i]).addScaledVector(_dir, h);
        P[i + 1].y = fm;
      }
    }
  }
  // A BOX (a chair seat, a step, a table): the segment is stopped at the face it CAME IN through.
  // Cloth landing on the seat lies across it — outward from her middle — and tips over the edge;
  // cloth beside it is held off that side. (Pushing out of the NEAREST face, 2026-09-27, shoved
  // the back panels down through the seat and out of its walls at floor level.)
  const _bx = V(), _bd = V(), _bq = V();
  // (o = the cloth's offset from the chain line: the box is tested against the CLOTH)
  // Returns the segment's length: on a seat, cloth BUNCHES at the edge instead of standing out
  // past it (a 20 cm segment starting 6 cm from the edge made a stiff shelf; bunching the joint
  // back to the edge lets the next segment hang straight down — the weights blend along the
  // chain, so the cloth between two joints compresses with them).
  function boxContact(ch, i, box, L, m, o, s) {
    const P = ch.P, a = P[i], b = P[i + 1];
    const k = ch.inset ? (ch.inset[i] || 0) * s : 0, L0 = ch.lens[i] * s;
    const x0 = box.min.x - m - o.x, x1 = box.max.x + m - o.x, y1 = box.max.y + m - o.y, z0 = box.min.z - m - o.z, z1 = box.max.z + m - o.z, yb = box.min.y - o.y;
    for (let probe = 0; probe < 2; probe++) {             // the segment's middle, then its end
      if (probe === 0) _bx.addVectors(a, b).multiplyScalar(0.5); else _bx.copy(b);
      if (!(_bx.x > x0 && _bx.x < x1 && _bx.y < y1 && _bx.y > yb && _bx.z > z0 && _bx.z < z1)) continue;
      // the face it came in through: the slab the segment a→x entered LAST (Liang–Barsky)
      _bd.subVectors(_bx, a);
      let face = 0, tin = -Infinity, t;                   // 1 x0, 2 x1, 3 z0, 4 z1, 5 the top
      if (a.x < x0 && _bd.x > 1e-9) { t = (x0 - a.x) / _bd.x; if (t > tin) { tin = t; face = 1; } }
      else if (a.x > x1 && _bd.x < -1e-9) { t = (x1 - a.x) / _bd.x; if (t > tin) { tin = t; face = 2; } }
      if (a.z < z0 && _bd.z > 1e-9) { t = (z0 - a.z) / _bd.z; if (t > tin) { tin = t; face = 3; } }
      else if (a.z > z1 && _bd.z < -1e-9) { t = (z1 - a.z) / _bd.z; if (t > tin) { tin = t; face = 4; } }
      if (a.y >= y1 - 1e-5 && _bd.y < -1e-9) { t = (y1 - a.y) / _bd.y; if (t > tin) { tin = t; face = 5; } }
      // a starts inside only because the cloth's offset raised the seat past it (her waist, where
      // the ring stands off her hips): it is still ON the seat
      if (!face && a.y > box.max.y && a.x > x0 && a.x < x1 && a.z > z0 && a.z < z1) face = 5;
      if (!face) {
        // it started inside (her hips sank into the seat): out of the nearest face, never down
        const dx0 = _bx.x - x0, dx1 = x1 - _bx.x, dy1 = y1 - _bx.y, dz0 = _bx.z - z0, dz1 = z1 - _bx.z;
        const mm = Math.min(dx0, dx1, dy1, dz0, dz1), kk = probe === 0 ? 2 : 1;
        if (mm === dy1) b.y += dy1 * kk; else if (mm === dx0) b.x -= dx0 * kk; else if (mm === dx1) b.x += dx1 * kk;
        else if (mm === dz0) b.z -= dz0 * kk; else b.z += dz1 * kk;
        relength(P, i, L);
        continue;
      }
      if (face < 5) {
        // BESIDE IT: held off that side
        const kk = probe === 0 ? 2 : 1;
        if (face === 1) b.x += (x0 - _bx.x) * kk; else if (face === 2) b.x += (x1 - _bx.x) * kk;
        else if (face === 3) b.z += (z0 - _bx.z) * kk; else b.z += (z1 - _bx.z) * kk;
        relength(P, i, L);
        continue;
      }
      // ON THE SEAT. Lying flat, the cloth's offset points straight down: the chain rides k above
      // the seat, and hanging off an edge the cloth is k inside the chain, so the chain's edge is
      // k further out than the seat's
      const ht = box.max.y + m + k;
      const ex0 = box.min.x - m - k, ex1 = box.max.x + m + k, ez0 = box.min.z - m - k, ez1 = box.max.z + m + k;
      const dy = a.y - ht, h = Math.sqrt(Math.max(0, L * L - dy * dy));        // (a may sit just under it)
      const onTop = a.x > ex0 && a.x < ex1 && a.z > ez0 && a.z < ez1;
      if (!onTop) {
        // its root is OFF the seat (just past an edge it bunched at, or a knee out past a bench's
        // end): it hangs straight down beside it. (Laying it back across the top, 2026-09-27,
        // flipped a chain between the two every frame: a 23 cm flicker in a held sit.)
        b.copy(a); b.y -= L;
        return L;
      }
      // straight out the way this chain faces on her (its ring azimuth — neighbours then stay side
      // by side instead of crossing), leaning a little the way the cloth was already going
      _bq.copy(ch.inw).applyQuaternion(_hq).negate(); _bq.y = 0;
      if (_bq.lengthSq() < 1e-8) _bq.set(b.x - _hc.x, 0, b.z - _hc.z);
      if (_bq.lengthSq() < 1e-8) _bq.set(0, 0, -1);
      _bq.normalize();
      _bd.set(b.x - a.x, 0, b.z - a.z);
      if (_bd.lengthSq() > 1e-4 * L * L && _bd.normalize().dot(_bq) > 0.5) _bd.multiplyScalar(0.3).addScaledVector(_bq, 0.7); else _bd.copy(_bq);
      _bd.normalize();
      _bq.copy(a).addScaledVector(_bd, h); _bq.y = ht;
      if (_bq.x > ex0 && _bq.x < ex1 && _bq.z > ez0 && _bq.z < ez1) { b.copy(_bq); return L; }
      // …past the edge: out to the edge only (BUNCHED), and the next segment falls from there
      let te = Infinity;
      if (_bd.x > 1e-9) te = Math.min(te, (ex1 - a.x) / _bd.x); else if (_bd.x < -1e-9) te = Math.min(te, (ex0 - a.x) / _bd.x);
      if (_bd.z > 1e-9) te = Math.min(te, (ez1 - a.z) / _bd.z); else if (_bd.z < -1e-9) te = Math.min(te, (ez0 - a.z) / _bd.z);
      te = Math.max(0, te) + 0.002 * s;
      _bq.copy(a).addScaledVector(_bd, te); _bq.y = ht;                           // just past the edge
      const d = _bq.distanceTo(a);
      if (i < ch.n - 1 && d >= 0.3 * L0) {
        if (d < L) { b.copy(_bq); return d; }
        b.copy(a).addScaledVector(_bq.sub(a).normalize(), L); return L;          // (never longer than it is)
      }
      // AT the edge already (or the last segment, with no joint below to bunch toward): it hangs
      // straight off the edge — hanging, the cloth is k inside the chain, which puts it just
      // outside the seat's side (lying it out flat here made a stiff 17 cm shelf)
      if (te < L) { b.copy(a).addScaledVector(_bd, te); b.y -= Math.sqrt(L * L - te * te); return L; }
      b.copy(a).addScaledVector(_bd, L);
      return L;
    }
    return L;
  }
  function relength(P, i, L) { _dir.subVectors(P[i + 1], P[i]).normalize(); P[i + 1].copy(P[i]).addScaledVector(_dir, L); }
  // neighbouring chains of one panel may not drift apart (the hem tore between them)
  function rings(fam, s, fl) {
    const lim = fam.F.ringMax || 1.25;
    for (let it = 0; it < 3; it++) {
      for (const pr of fam.pairs) {
        const { a, b, rd } = pr;
        for (let i = 1; i < rd.length; i++) {
          _tmp.subVectors(b.P[i], a.P[i]);
          const d = _tmp.length(), m = rd[i] * s * lim;
          if (d > m && d > 1e-6) { _tmp.multiplyScalar((d - m) / d * 0.5); a.P[i].add(_tmp); b.P[i].sub(_tmp); }
        }
      }
      for (const g of fam.groups) for (const ch of g) {
        for (let i = 0; i < ch.n; i++) { relength(ch.P, i, ch.cl[i]); if (it === 2) contact(ch, i, s, fl); }
      }
    }
  }
  // points → rotations, top-down
  function chainWrite(ch) {
    const { bones, n, P, D, W } = ch;
    if (!Number.isFinite(P[n].x + P[n].y + P[n].z)) { ch.init = false; return; }     // (never write a NaN into a bone)
    for (let i = 0; i < n; i++) {
      const b = bones[i];
      // a bunched segment: its child joint slides back along it (the cloth between compresses)
      if (i < n - 1) {
        const c = bones[i + 1], f = ch.cl[i] / Math.max(ch.lens[i] * _sNow, 1e-9);
        if (f < 0.999 || c.position.distanceToSquared(ch.loc[i + 1]) > 1e-12) { touchPos(c); c.position.copy(ch.loc[i + 1]).multiplyScalar(Math.min(1, f)); }
      }
      b.getWorldPosition(_a);
      if (i < n - 1) bones[i + 1].getWorldPosition(_b); else { _b.copy(ch.tipLocal); b.localToWorld(_b); }
      _cur.subVectors(_b, _a).normalize();
      _dir.subVectors(P[i + 1], P[i]).normalize();
      // contact overruled the spring: its velocity must not push back into the collider
      if (D[i].angleTo(_dir) > 0.05) W[i].multiplyScalar(0.3);
      D[i].copy(_dir);
      _q1.setFromUnitVectors(_cur, _dir);
      worldQ(b, _q2); _q1.multiply(_q2);
      worldQ(b.parent, _q2).invert();
      b.quaternion.copy(_q2.multiply(_q1));
      b.updateMatrixWorld(true);
    }
  }

  // measure her for the arm clearance NOW (≈0.1 s once), not on the first frame a clip plays (a
  // hitch mid-scene — the final review); if she isn't ready, it's measured lazily later
  if (opts.clearance !== false) { try { clearBuild(); if (!CLR.ok) CLR.dirty = true; } catch (e) { CLR.dirty = true; } }
  return rig;
}

// ═══ THE WARDROBE: attachPart ═══
// A garment (or hairstyle, or armour) is its OWN glb, skinned to the SAME joint names — made by
// the rig-up's bind_garment + extract_parts, or any tool that skins to the IEH names. It snaps
// onto a body by bone NAME: no re-rig, no re-export of her.
//   const worn = attachPart(bodyRoot, partScene, { replace: ["vael_skirt"], dress: (mesh) => {}, fit: formPart })
//   worn.off()          takes it off and puts back what it replaced
// · a bone the body lacks (a ponytail's) is GRAFTED on, under its parent by name, at its rest
//   offset — an identity child, so her own skin never changes (THE PARITY LAW)
// · the part's `ieh_wear` extras tell the rig which skirt-ring chains its cloth rides on and
//   which chains it brings (rig.wear) — the physics follows the clothes
// · dress(mesh) is the game's look (toon + ink), called once each mesh sits on the body
export function attachPart(root, part, o = {}) {
  const B = {};
  let host = null, rig = null;
  root.traverse((b) => { if (b.isBone) B[b.name] = b; if (!host && b.isSkinnedMesh) host = b; if (!rig && b.userData && b.userData.rig) rig = b.userData.rig; });
  if (!host) return null;
  let info = null;
  part.traverse((n) => { if (!info && n.userData && n.userData.ieh_wear) info = n.userData.ieh_wear; });
  const meshes = [];
  part.traverse((m) => { if (m.isSkinnedMesh) meshes.push(m); });
  if (!meshes.length) return null;                 // (a part already worn and taken off has none left)
  const name = (info && info.garment) || meshes[0].name || "part";
  // what she has on: every worn part, in order (root.userData.wardrobe) — parts come off in any order
  if (!root.userData.wardrobe) Object.defineProperty(root.userData, "wardrobe", { value: [], enumerable: false });   // (not cloned/serialised)
  const wardrobe = root.userData.wardrobe;
  const grafted = [];
  const graft = (pb) => {                           // the part's bone → the body's, grafting if new
    if (B[pb.name]) return B[pb.name];
    const par = pb.parent && pb.parent.isBone ? graft(pb.parent) : null;
    if (!par) return null;
    const nb = new THREE.Bone();
    nb.name = pb.name;
    nb.position.copy(pb.position); nb.quaternion.copy(pb.quaternion); nb.scale.copy(pb.scale);
    par.add(nb); nb.updateMatrixWorld(true);
    B[nb.name] = nb; grafted.push(nb);
    if (rig && rig.B) rig.B[nb.name] = nb;
    return nb;
  };
  // the joints its chains name get grafted too, even one no vertex is weighted to (a bow's tip)
  for (const fam of Object.values((info && info.chains) || {})) for (const C of Object.values(fam.chains || {})) for (const jn of C.joints || []) {
    if (B[jn]) continue;
    let pb = null; part.traverse((n) => { if (!pb && n.isBone && n.name === jn) pb = n; });
    if (pb) graft(pb); else console.warn("[ieRig] attachPart: " + name + " names joint " + jn + " but carries no such bone");
  }
  // hide what it replaces (and their ink) FIRST — the new part may carry the same mesh names. A
  // name is a garment's (a part's) or a mesh's; the physics comes off by garment
  const hidden = [], restore = [], off = new Set();
  for (const r of o.replace || []) {
    let found = false;
    root.traverse((m) => {
      if (!m.isMesh || !m.visible) return;
      const g = m.userData.garment || m.name.replace(/_ink$/, "");
      if (g === r || m.name === r || m.name === r + "_ink") { m.visible = false; hidden.push(m); off.add(g); found = true; }
    });
    if (!found) off.add(r);                        // (nothing drawn by that name: its physics still comes off)
  }
  for (const g of off) if (rig) { const w = rig.wearing(g); if (w) restore.push([g, w]); rig.wear(g, null); }
  const hs = host.skeleton;
  const added = [], owned = [];
  const close = (A, Bm) => A.elements.every((v, i) => Math.abs(v - Bm.elements[i]) < 1e-5);
  for (const m of meshes) {
    const bones = m.skeleton.bones.map(graft);
    if (bones.some((b) => !b)) { console.warn("[ieRig] attachPart: " + m.name + " is skinned to bones this body can't take"); continue; }
    // a FORMED body (ieForm.js): o.fit(root, mesh, grafted, info) = ieForm.formPart — the garment is fitted to her
    // proportions first and its binds become hers (passed in, so this file stays standalone for other readers)
    if (root.userData.form && o.fit) { try { o.fit(root, m, grafted, info); } catch (e) { console.warn("[ieRig] attachPart: " + m.name + " could not be fitted to her form", e); } }
    // the SAME joints (and the same binds) as her own meshes → share her skeleton (one bone texture)
    const same = bones.length === hs.bones.length && bones.every((b, i) => b === hs.bones[i] && close(m.skeleton.boneInverses[i], hs.boneInverses[i]));
    const sk = same ? hs : new THREE.Skeleton(bones, m.skeleton.boneInverses.map((x) => x.clone()));
    if (!same) owned.push(sk);
    const bind = m.bindMatrix.clone();
    m.removeFromParent();
    host.parent.add(m);
    m.bind(sk, bind);
    m.frustumCulled = false;
    const before = new Set(host.parent.children);
    if (o.dress) o.dress(m);
    const extra = host.parent.children.filter((c) => !before.has(c));   // its ink hull, say
    for (const x of [m, ...extra]) { x.userData.garment = name; added.push(x); }
  }
  if (rig && info) rig.wear(name, info);
  if (rig && rig.refit) rig.refit();                // the arm clearance re-measures her new clothes
  let done = false;
  const worn = {
    name, meshes: added, hidden, grafted, info, restore,
    off() {
      if (done) return;
      done = true;
      const at = wardrobe.indexOf(worn); if (at >= 0) wardrobe.splice(at, 1);
      for (const m of added) m.removeFromParent();
      for (const sk of owned) sk.dispose();
      // a later part that replaced THIS one took its SLOT: it inherits what this one replaced
      // (the coat comes off under the cape → the skirt stays hidden, the cape still covers it)
      const heir = wardrobe.find((w) => w.hidden.some((m) => added.includes(m)));
      for (const w of wardrobe) {
        for (let j = w.hidden.length - 1; j >= 0; j--) if (added.includes(w.hidden[j])) w.hidden.splice(j, 1);
        for (let j = w.restore.length - 1; j >= 0; j--) if (w.restore[j][0] === name) w.restore.splice(j, 1);
      }
      if (rig) rig.wear(name, null);
      if (heir) { heir.hidden.push(...hidden); heir.restore.push(...restore); return; }
      // put back what it replaced — unless another part still has it replaced
      for (const m of hidden) if (!wardrobe.some((w) => w.hidden.includes(m))) m.visible = true;
      if (rig && rig.refit) rig.refit();
      if (rig) for (const [g, w] of restore) if (!wardrobe.some((x) => x.restore.some(([n]) => n === g))) rig.wear(g, w);
      // (grafted bones stay: identity children with no skin, harmless, and the next part may use them)
    },
  };
  wardrobe.push(worn);
  return worn;
}
